import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../../../config/supabase';
import { useCollection } from '../../../hooks/useCollection';
import { grantReferralReward, getReferralConfig, saveReferralConfig, referralLink, buildReferralRows, DEFAULT_FREE_DAYS } from '../utils/referralService';
import BlacklistManager from '../../blacklist/components/BlacklistManager';
import BlacklistApprovalCenter from '../../blacklist/components/BlacklistApprovalCenter';
import { isHiddenModule } from '../../../config/hiddenModules';

// Helper to get currency from settings
const getCurrency = () => {
  try {
    const saved = localStorage.getItem('appSettings');
    if (saved) {
      return JSON.parse(saved).currency || 'MAD';
    }
  } catch (e) {}
  return 'MAD';
};

// Constants
const ALL_MODULES = [
  { id: 'clients',      label: 'Clients',         emoji: '👥' },
  { id: 'cars',         label: 'Voitures',        emoji: '🚗' },
  { id: 'rentals',      label: 'Contrats',        emoji: '📄' },
  { id: 'calendar',     label: 'Calendrier',      emoji: '📅' },
  { id: 'vidange',      label: 'Vidanges',        emoji: '🔧' },
  { id: 'cheques',      label: 'Chèques',         emoji: '💳' },
  { id: 'staff',        label: 'Personnel',       emoji: '👔' },
  { id: 'mokhalafat',   label: 'Contraventions',  emoji: '⚠️' },
  { id: 'reservations', label: 'Réservations',    emoji: '📆' },
  { id: 'invoices',     label: 'Factures',        emoji: '🧾' },
  { id: 'finances',     label: 'Finances',        emoji: '💰' },
  { id: 'history',      label: 'Historique',      emoji: '🕐' },
  { id: 'gps',          label: 'GPS Tracking',    emoji: '📍' },
];

const ALL_APIS = [
  { id: 'geniusScan',  label: 'Genius Scan',      emoji: '🔬', desc: 'Scanner pro: auto-crop + enhancement' },
  { id: 'sms',         label: 'SMS Alerts',       emoji: '💬', desc: 'Notifications SMS aux clients' },
  { id: 'gps_live',    label: 'GPS Live',         emoji: '🛰️', desc: 'Suivi GPS temps réel avancé' },
];

const SUBSCRIPTION_PLANS = [
  { id: '1month',  label: '1 Mois',  months: 1,  price: 0, accent: '#6366f1', badge: null },
  { id: '2months', label: '2 Mois',  months: 2,  price: 0, accent: '#3b82f6', badge: null },
  { id: '6months', label: '6 Mois',  months: 6,  price: 0, accent: '#0ea5e9', badge: 'Populaire' },
  { id: '1year',   label: '1 An',    months: 12, price: 0, accent: '#f59e0b', badge: 'Meilleur prix' },
];

const SA_PERMISSIONS = [
  { id: 'dashboard',      label: 'Dashboard',           emoji: '📊' },
  { id: 'agencies',       label: 'Gérer agences',        emoji: '🏢' },
  { id: 'registrations',  label: 'Inscriptions',         emoji: '📋' },
  { id: 'subscriptions',  label: 'Abonnements & revenus',emoji: '💳' },
  { id: 'create_agency',  label: 'Créer des agences',    emoji: '➕' },
  { id: 'global_config',  label: 'Config globale',       emoji: '⚙️' },
  { id: 'co_admins',      label: 'Gérer co-admins',      emoji: '👥' },
];

let __SA_TOKEN__ = sessionStorage.getItem('sa_token') || '';

async function confirmUserEmail(uid) {
  try {
    await fetch('/api/sa/confirm-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ uid, saToken: __SA_TOKEN__ })
    });
  } catch (_) {}
}

export default function SuperAdminApp() {
  const [authed, setAuthed] = useState(() => {
    return sessionStorage.getItem('sa_authed') === 'yes';
  });
  const [currentCoAdmin, setCurrentCoAdmin] = useState(() => {
    try { 
      return JSON.parse(sessionStorage.getItem('sa_coadmin') || 'null'); 
    } catch { 
      return null; 
    }
  });
  const [coAdmins, setCoAdmins] = useState([]);
  const [referralFreeDays, setReferralFreeDays] = useState(DEFAULT_FREE_DAYS);
  const [referralCash, setReferralCash] = useState(200);
  const [referralCurrency, setReferralCurrency] = useState('DH');
  const [referralSaved, setReferralSaved] = useState(false);
  const [password, setPassword] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [authError, setAuthError] = useState('');
  // Telegram OTP (2FA) login step
  const [otpStep, setOtpStep] = useState(false);
  const [otpId, setOtpId] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [tab, setTab] = useState('dashboard');

  const hasPerm = (perm) => {
    if (!currentCoAdmin) return true; // main SA = all permissions
    return (currentCoAdmin.permissions || []).includes(perm);
  };

  const [dm, setDm] = useState(() => localStorage.getItem('sa_dark') === '1');
  const [saMobileOpen, setSaMobileOpen] = useState(false);
  const toggleDark = () => setDm(v => { 
    localStorage.setItem('sa_dark', v ? '0' : '1'); 
    return !v; 
  });

  // Theme tokens
  const T = {
    bg:        dm ? '#0f172a' : '#f1f5f9',
    sidebar:   dm ? '#1e293b' : '#ffffff',
    card:      dm ? '#1e293b' : '#ffffff',
    cardBorder:dm ? '#334155' : '#e2e8f0',
    text:      dm ? '#f1f5f9' : '#0f172a',
    textSub:   dm ? '#94a3b8' : '#64748b',
    textMuted: dm ? '#64748b' : '#94a3b8',
    input:     dm ? '#0f172a' : '#f8fafc',
    inputBorder:dm? '#334155' : '#e2e8f0',
    hover:     dm ? '#334155' : '#f1f5f9',
    accent:    '#3b82f6',
    divider:   dm ? '#334155' : '#e2e8f0',
  };

  const [registrations, setRegistrations] = useState([]);
  const [expandedReg, setExpandedReg] = useState(null);
  const [regModules, setRegModules] = useState({});
  const [defaultMods, setDefaultMods] = useState(new Set(ALL_MODULES.map(m => m.id)));
  const [defaultApis, setDefaultApis] = useState(new Set());
  const [regApis, setRegApis] = useState({});
  const [savingModules, setSavingModules] = useState(null);
  const [plans, setPlans] = useState(SUBSCRIPTION_PLANS);
  const [search, setSearch] = useState('');
  const [regFilter, setRegFilter] = useState('pending'); // pending | approved | rejected | all
  const [showRenew, setShowRenew] = useState(null);
  const [editAgency, setEditAgency] = useState(null);
  const [showRegisterAgency, setShowRegisterAgency] = useState(false);
  const [impersonating, setImpersonating] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);

  // ── "Online Agencies Now" presence ──
  // An agency is online if its data.lastActiveAt is within this window.
  const ONLINE_WINDOW_MS = 5 * 60 * 1000; // 5 minutes (configurable)
  const [nowTick, setNowTick] = useState(Date.now());
  useEffect(() => {
    if (!authed) return;
    const id = setInterval(() => setNowTick(Date.now()), 30 * 1000); // refresh every 30s
    return () => clearInterval(id);
  }, [authed]);

  // Telegram Broadcast (Super Admin only)
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const [broadcastResult, setBroadcastResult] = useState(null);

  const sendBroadcast = async () => {
    const msg = broadcastMsg.trim();
    if (!msg) { alert('✍️ Écrivez un message d\'abord.'); return; }
    if (!confirm('📢 Envoyer ce message à TOUTES les agences actives via Telegram ?')) return;
    setBroadcastBusy(true);
    setBroadcastResult(null);
    try {
      const r = await fetch('/api/sa/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg, saToken: __SA_TOKEN__ }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { alert('❌ ' + (d.error || 'Erreur lors de l\'envoi.')); }
      else setBroadcastResult(d);
    } catch (e) {
      alert('❌ Erreur réseau: ' + e.message);
    }
    setBroadcastBusy(false);
  };

  // Derive accounts dynamically from registrations state
  const accounts = useMemo(() => {
    return registrations
      .filter(r => r.status === 'approved')
      .map(r => ({
        id: r.uid,
        uid: r.uid,
        email: r.email || '',
        agencyName: r.agencyName || r.name || '',
        name: r.name || r.agencyName || '',
        phone: r.phone || '',
        city: r.data?.city || '',
        maxUsers: r.data?.maxUsers || 1,
        status: r.data?.status || 'active',
        notes: r.data?.notes || '',
        subscriptionPlan: r.data?.subscriptionPlan || '',
        subscriptionEnd: r.data?.subscriptionEnd || '',
        payments: r.data?.payments || [],
        createdAt: r.data?.createdAt || r.id,
        updatedAt: r.data?.updatedAt || null,
        referredBy: r.data?.referredBy || '',
        referralRewarded: !!r.data?.referralRewarded,
        referralRewardedAt: r.data?.referralRewardedAt || null,
        referralRewards: r.data?.referralRewards || [],
        referralFreeDaysTotal: r.data?.referralFreeDaysTotal || 0,
        enabledModules: r.enabledModules,
        enabledApis: r.enabledApis,
        data: r.data || {}
      }));
  }, [registrations]);

  const deleteAgency = async (agencyId) => {
    if (!confirm("⚠️ ATTENTION: Voulez-vous vraiment supprimer cette agence ? Cette action est irréversible et supprimera TOUTES les données de cette agence (voitures, contrats, clients, personnel, finances, etc.).")) return;
    
    try {
      // 1. Delete from agency-specific tables
      const tables = ['cars', 'clients', 'rentals', 'vidanges', 'cheques', 'staff', 'staff_payments', 'client_payments', 'reservations', 'mokhalafat', 'expenses'];
      for (const table of tables) {
        await supabase.from(table).delete().eq('agency_id', agencyId);
      }
      
      // 2. Delete the user registration
      await supabase.from('user_registrations').delete().eq('uid', agencyId);
      
      // 3. Delete the auth user via server endpoint
      const saToken = __SA_TOKEN__ || '';
      const res = await fetch('/api/sa/delete-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid: agencyId, saToken })
      });
      
      if (!res.ok) {
        const data = await res.json();
        console.warn('Could not delete Auth user, might need Service Role Key:', data.error);
      }

      alert("✅ Agence supprimée avec succès.");
      if (fetchRegsRef.current) fetchRegsRef.current();
    } catch (e) {
      alert("❌ Erreur lors de la suppression: " + e.message);
    }
  };

  // Load pending registrations
  const fetchRegsRef = useRef(null);
  useEffect(() => {
    if (!authed) return;
    let regChannel = null;
    const fetchRegs = async () => {
      const { data } = await supabase.from('user_registrations').select('*');
      if (data) setRegistrations(data.map(r => ({
        id: r.id, uid: r.uid,
        email: r.data?.email || r.email || r.uid,
        name: r.data?.name || r.data?.agencyName || r.uid,
        agencyName: r.data?.agencyName || r.data?.name || r.uid,
        phone: r.data?.phone || '',
        status: r.status,
        agencyId: r.agency_id,
        enabledModules: r.enabled_modules,
        enabledApis: r.enabled_apis,
        createdAt: r.data?.createdAt || r.id,
        data: r.data || {}
      })));
    };
    fetchRegsRef.current = fetchRegs;
    fetchRegs();
    regChannel = supabase
      .channel('sa_regs')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_registrations' }, fetchRegs)
      .subscribe();
    return () => { 
      if (regChannel) supabase.removeChannel(regChannel); 
    };
  }, [authed]);

  // Load plan pricing and default modules
  useEffect(() => {
    if (!authed) return;
    const loadPlans = async () => {
      try {
        const { data: settingsRow } = await supabase.from('sa_settings').select('*').eq('id', 'plans').single();
        if (settingsRow?.data?.plans_v2) {
          setPlans(settingsRow.data.plans_v2);
        } else if (settingsRow?.data) {
          setPlans(SUBSCRIPTION_PLANS.map(p => ({ ...p, price: settingsRow.data[p.id] || 0 })));
        }
      } catch {}
    };
    const loadDefaults = async () => {
      try {
        const { data: defRow } = await supabase.from('sa_settings').select('*').eq('id', 'default_modules').single();
        if (defRow?.data?.modules) setDefaultMods(new Set(defRow.data.modules));
        if (defRow?.data?.apis) setDefaultApis(new Set(defRow.data.apis));
      } catch {}
    };
    loadPlans();
    loadDefaults();
  }, [authed]);

  const saveDefaultModules = async (mods, apis) => {
    await supabase.from('sa_settings').upsert({ id: 'default_modules', data: { modules: [...mods], apis: [...apis] } });
    setDefaultMods(new Set(mods));
    setDefaultApis(new Set(apis));
    alert('✅ Modules par défaut sauvegardés!');
  };

  // Load co-admins
  useEffect(() => {
    if (!authed) return;
    const loadCoAdmins = async () => {
      try {
        const { data } = await supabase.from('sa_settings').select('*').eq('id', 'co_admins').single();
        if (data?.data?.list) setCoAdmins(data.data.list);
      } catch {}
    };
    loadCoAdmins();
  }, [authed]);

  // Load referral config (free days / cash per successful referral).
  useEffect(() => {
    if (!authed) return;
    getReferralConfig(supabase).then(c => {
      setReferralFreeDays(c.freeDays); setReferralCash(c.cashAmount); setReferralCurrency(c.currency);
    }).catch(() => {});
  }, [authed]);

  const saveReferralDays = async () => {
    try {
      await saveReferralConfig(supabase, { freeDays: referralFreeDays, cashAmount: referralCash, currency: referralCurrency });
      setReferralSaved(true);
      setTimeout(() => setReferralSaved(false), 2000);
    } catch (e) { alert('❌ ' + e.message); }
  };

  const saveCoAdmins = async (list) => {
    await supabase.from('sa_settings').upsert({ id: 'co_admins', data: { list } });
    setCoAdmins(list);
  };

  const initSaSession = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        await supabase.auth.signInAnonymously();
        await new Promise(r => setTimeout(r, 500));
      }
    } catch {}
  };

  // Grant access with the server-issued signed session token.
  const grantSaAccess = async (token) => {
    __SA_TOKEN__ = token || '';
    try { sessionStorage.setItem('sa_token', __SA_TOKEN__); } catch (_) {}
    await initSaSession();
    setCurrentCoAdmin(null);
    sessionStorage.removeItem('sa_coadmin');
    setOtpStep(false); setOtpId(''); setOtpCode('');
    setAuthed(true);
    sessionStorage.setItem('sa_authed', 'yes');
    setAuthError('');
  };

  // Step 2: verify the Telegram OTP.
  const verifyOtp = async () => {
    setAuthBusy(true);
    try {
      const r = await fetch('/api/sa/verify-otp', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ otpId, otp: otpCode.trim() }),
      });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.ok) { await grantSaAccess(body.token); return; }
      setAuthError('❌ ' + (body.error || 'Code incorrect'));
    } catch (_) {
      setAuthError('❌ Erreur réseau');
    } finally {
      setAuthBusy(false);
    }
  };

  const login = async () => {
    // Super Admin path (no co-admin email): password → Telegram OTP.
    if (!loginEmail && password) {
      setAuthBusy(true);
      try {
        const r = await fetch('/api/sa/request-otp', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password }),
        });
        const body = await r.json().catch(() => ({}));
        if (r.ok && body.ok) {
          if (body.otpRequired) { setOtpId(body.otpId); setOtpStep(true); setAuthError(''); return; }
          await grantSaAccess(body.token); // 2FA not configured → direct
          return;
        }
        setAuthError('❌ ' + (body.error || 'Mot de passe incorrect'));
        return;
      } catch (_) {
        setAuthError('❌ Erreur réseau');
        return;
      } finally {
        setAuthBusy(false);
      }
    }

    if (loginEmail) {
      try {
        const { data } = await supabase.from('sa_settings').select('*').eq('id', 'co_admins').single();
        const list = data?.data?.list || [];
        const admin = list.find(a => a.email === loginEmail.trim() && a.password === password);
        if (admin) {
          await initSaSession();
          setCurrentCoAdmin(admin);
          sessionStorage.setItem('sa_coadmin', JSON.stringify(admin));
          setAuthed(true);
          sessionStorage.setItem('sa_authed', 'yes');
          setAuthError('');
          return;
        }
      } catch {}
    }
    setAuthError('❌ Identifiants incorrects');
  };

  // ===== LOGIN SCREEN =====
  if (!authed) {
    return (
      <div style={{ minHeight: '100vh', background: dm ? 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)' : 'linear-gradient(135deg,#eff6ff 0%,#dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <button onClick={toggleDark} style={{ position: 'fixed', top: '16px', right: '16px', background: T.sidebar, border: `1px solid ${T.cardBorder}`, borderRadius: '10px', padding: '8px 12px', cursor: 'pointer', fontSize: '16px', color: T.text }}>
          {dm ? '☀️' : '🌙'}
        </button>
        <div style={{ background: T.sidebar, borderRadius: '24px', boxShadow: dm ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(0,0,0,0.12)', padding: '48px 40px', width: '100%', maxWidth: '380px', textAlign: 'center', border: `1px solid ${T.cardBorder}` }}>
          <div style={{ width: '64px', height: '64px', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: '28px', boxShadow: '0 8px 20px rgba(59,130,246,0.4)' }}>👑</div>
          <h1 style={{ fontSize: '24px', fontWeight: 900, color: T.text, margin: '0 0 6px' }}>Super Admin</h1>
          <p style={{ fontSize: '14px', color: T.textSub, margin: '0 0 28px' }}>CAR TRACK — Gestion des agences</p>
          {!otpStep ? (
            <>
              <input
                type="email"
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                placeholder="✉️ Email co-admin (vide = Super Admin)"
                style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: `2px solid ${T.inputBorder}`, background: T.input, color: T.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '10px' }}
              />
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && login()}
                placeholder="🔑 Mot de passe"
                autoFocus
                style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: `2px solid ${authError ? '#ef4444' : T.inputBorder}`, background: T.input, color: T.text, fontSize: '14px', outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }}
              />
              {authError && <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 12px', fontWeight: 600 }}>{authError}</p>}
              <button
                onClick={login}
                disabled={authBusy}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', color: 'white', fontWeight: 800, fontSize: '15px', border: 'none', cursor: authBusy ? 'wait' : 'pointer', boxShadow: '0 4px 15px rgba(59,130,246,0.4)', opacity: authBusy ? 0.7 : 1 }}
              >
                {authBusy ? '⏳ ...' : '🔓 Connexion'}
              </button>
            </>
          ) : (
            <>
              <p style={{ fontSize: '13px', color: T.textSub, margin: '0 0 14px' }}>
                📲 Un code à 6 chiffres a été envoyé sur votre Telegram. Saisissez-le ci-dessous.
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={otpCode}
                onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={e => e.key === 'Enter' && verifyOtp()}
                placeholder="• • • • • •"
                autoFocus
                style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: `2px solid ${authError ? '#ef4444' : T.inputBorder}`, background: T.input, color: T.text, fontSize: '22px', letterSpacing: '8px', textAlign: 'center', fontWeight: 800, outline: 'none', boxSizing: 'border-box', marginBottom: '12px' }}
              />
              {authError && <p style={{ color: '#ef4444', fontSize: '13px', margin: '0 0 12px', fontWeight: 600 }}>{authError}</p>}
              <button
                onClick={verifyOtp}
                disabled={authBusy || otpCode.length < 6}
                style={{ width: '100%', padding: '14px', borderRadius: '12px', background: 'linear-gradient(135deg,#10b981,#059669)', color: 'white', fontWeight: 800, fontSize: '15px', border: 'none', cursor: (authBusy || otpCode.length < 6) ? 'not-allowed' : 'pointer', boxShadow: '0 4px 15px rgba(16,185,129,0.4)', opacity: (authBusy || otpCode.length < 6) ? 0.6 : 1, marginBottom: '10px' }}
              >
                {authBusy ? '⏳ Vérification...' : '✅ Vérifier le code'}
              </button>
              <button
                onClick={() => { setOtpStep(false); setOtpCode(''); setOtpId(''); setAuthError(''); }}
                style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'transparent', color: T.textSub, fontWeight: 600, fontSize: '13px', border: `1px solid ${T.cardBorder}`, cursor: 'pointer' }}
              >
                ← Retour
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // If impersonating
  if (impersonating) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: dm ? '#0f172a' : '#f8fafc' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e3a8a, #3b82f6)', color: 'white', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '20px' }}>👑</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: '14px' }}>Mode Support / Impersonation</div>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>Vous visualisez et contrôlez le compte de: <strong>{impersonating.agencyName || impersonating.name}</strong></div>
            </div>
          </div>
          <button 
            onClick={() => setImpersonating(null)}
            style={{ background: 'rgba(255, 255, 255, 0.2)', border: 'none', color: 'white', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 800, transition: 'all 0.2s', fontSize: '13px' }}
          >
            ✕ Quitter le mode support
          </button>
        </div>
        <iframe 
          src={`/?impersonate=${impersonating.uid}`}
          style={{ width: '100%', flex: 1, border: 'none' }}
          title="Impersonation"
        />
      </div>
    );
  }

  const now = new Date();
  const daysLeft = (a) => {
    if (!a.subscriptionEnd) return null;
    return Math.ceil((new Date(a.subscriptionEnd) - now) / (1000 * 60 * 60 * 24));
  };

  const savePlanPrices = async (updatedPlans) => {
    setSavingPlan(true);
    try {
      await supabase.from('sa_settings').upsert({ id: 'plans', data: { plans_v2: updatedPlans } });
      setPlans(updatedPlans);
      alert('✅ Plans sauvegardés!');
    } catch (e) {
      alert('❌ ' + e.message);
    }
    setSavingPlan(false);
  };

  const pendingReg = registrations.filter(r => r.status === 'pending');
  const approved = registrations.filter(r => r.status === 'approved');
  const rejected = registrations.filter(r => r.status === 'rejected');

  const navItems = [
    { id: 'dashboard',     icon: '📊', label: 'Dashboard',    badge: null,                      perm: 'dashboard' },
    { id: 'agencies',      icon: '🏢', label: 'Agences',      badge: approved.length || null,   perm: 'agencies' },
    { id: 'registrations', icon: '📋', label: 'Inscriptions', badge: pendingReg.length || null, perm: 'registrations' },
    { id: 'users_staff',   icon: '👥', label: 'Membres & Personnel', badge: null,               perm: 'agencies' },
    { id: 'subscriptions', icon: '💳', label: 'Abonnements',  badge: null,                      perm: 'subscriptions' },
    { id: 'broadcast',     icon: '📢', label: 'Telegram Broadcast', badge: null,                 perm: null, saOnly: true },
    { id: 'referrals',     icon: '🎁', label: 'Parrainage',   badge: null,                      perm: 'agencies' },
    { id: 'blacklist',     icon: '🚫', label: 'Liste noire',  badge: null,                      perm: 'agencies' },
    { id: 'blacklist_approval', icon: '🛡️', label: 'Approbations LN', badge: null,               perm: 'agencies' },
    { id: 'settings',      icon: '⚙️', label: 'Paramètres',   badge: null,                      perm: null },
  ].filter(item => (!item.saOnly || !currentCoAdmin) && (!item.perm || hasPerm(item.perm)));

  const sidebarContent = () => (
    <React.Fragment>
      <div style={{ padding: '20px', borderBottom: `1px solid ${T.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '34px', height: '34px', background: currentCoAdmin ? 'linear-gradient(135deg,#f59e0b,#ef4444)' : 'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
            {currentCoAdmin ? '🛡️' : '👑'}
          </div>
          <div>
            <div style={{ fontWeight: 900, fontSize: '13px', color: T.text }}>
              {currentCoAdmin ? (currentCoAdmin.name || currentCoAdmin.email) : 'Super Admin'}
            </div>
            <div style={{ fontSize: '10px', color: T.textMuted }}>
              {currentCoAdmin ? '🔒 Co-Admin' : `${approved.length} agence(s)`}
            </div>
          </div>
        </div>
        <button onClick={() => setSaMobileOpen(false)} style={{ display: 'none', background: 'none', border: 'none', color: T.textSub, fontSize: '20px', cursor: 'pointer', padding: '0 4px' }} className="sa-close-btn">×</button>
      </div>
      <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
        {navItems.map(item => (
          <button 
            key={item.id}
            onClick={() => { setTab(item.id); setSaMobileOpen(false); }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 12px', borderRadius: '10px', border: 'none', cursor: 'pointer',
              marginBottom: '4px', fontSize: '13px', fontWeight: 700, transition: 'all 0.15s',
              background: tab === item.id ? '#3b82f6' : 'transparent',
              color: tab === item.id ? 'white' : T.textSub,
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>{item.icon}</span> <span>{item.label}</span>
            </span>
            {item.badge && (
              <span style={{ background: tab === item.id ? 'rgba(255,255,255,0.3)' : '#ef4444', color: 'white', borderRadius: '99px', fontSize: '10px', fontWeight: 800, padding: '1px 7px', minWidth: '18px', textAlign: 'center' }}>
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>
      <div style={{ padding: '10px', borderTop: `1px solid ${T.divider}`, display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <button onClick={toggleDark} style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: `1px solid ${T.divider}`, background: 'transparent', color: T.textSub, fontSize: '12px', fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
          {dm ? '☀️ Mode clair' : '🌙 Mode sombre'}
        </button>
        <button 
          onClick={() => { 
            __SA_TOKEN__ = ''; 
            setAuthed(false); 
            setCurrentCoAdmin(null); 
            sessionStorage.removeItem('sa_authed'); 
            sessionStorage.removeItem('sa_coadmin'); 
            sessionStorage.removeItem('sa_token'); 
          }} 
          style={{ width: '100%', padding: '8px 12px', borderRadius: '10px', border: 'none', background: dm ? '#450a0a' : '#fef2f2', color: '#ef4444', fontSize: '12px', fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}
        >
          🚪 Déconnexion
        </button>
      </div>
    </React.Fragment>
  );

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>
      <style dangerouslySetInnerHTML={{ __html: `
        .sa-sidebar { position: fixed; top: 0; left: 0; bottom: 0; width: 220px; background: ${T.sidebar}; border-right: 1px solid ${T.divider}; display: flex; flex-direction: column; z-index: 50; transition: transform 0.25s; }
        .sa-topbar { display: none; }
        .sa-content { margin-left: 220px; min-height: 100vh; }
        .sa-overlay { display: none; }
        @media (max-width: 768px) {
          .sa-sidebar { transform: translateX(-100%); box-shadow: 4px 0 20px rgba(0,0,0,0.3); }
          .sa-sidebar.open { transform: translateX(0); }
          .sa-close-btn { display: block !important; }
          .sa-topbar { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: ${T.sidebar}; border-bottom: 1px solid ${T.divider}; position: sticky; top: 0; z-index: 40; }
          .sa-content { margin-left: 0; padding-top: 0; }
          .sa-overlay { display: block; position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 45; }
        }
      `}} />

      {/* Sidebar */}
      <aside className={`sa-sidebar${saMobileOpen ? ' open' : ''}`}>
        {sidebarContent()}
      </aside>

      {/* Mobile Overlay */}
      {saMobileOpen && <div className="sa-overlay" onClick={() => setSaMobileOpen(false)} />}

      {/* Mobile Top Bar */}
      <div className="sa-topbar">
        <button onClick={() => setSaMobileOpen(true)} style={{ background: 'none', border: 'none', color: T.text, fontSize: '22px', cursor: 'pointer', padding: '0' }}>☰</button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '28px', height: '28px', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>👑</div>
          <span style={{ fontWeight: 900, fontSize: '14px', color: T.text }}>Super Admin</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {pendingReg.length > 0 && (
            <span style={{ background: '#ef4444', color: 'white', borderRadius: '99px', fontSize: '10px', fontWeight: 800, padding: '2px 7px' }}>
              {pendingReg.length}
            </span>
          )}
          <button onClick={toggleDark} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' }}>
            {dm ? '☀️' : '🌙'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="sa-content">
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '20px 16px' }}>

          {/* TAB: DASHBOARD */}
          {tab === 'dashboard' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '22px', fontWeight: 900, color: T.text }}>Dashboard</div>
                <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '2px' }}>Bienvenue dans CAR TRACK — Super Admin Panel</div>
              </div>

              {/* Stats Grid */}
              {(() => {
                const totalRevenue = accounts.reduce((sum, a) => sum + (a.payments || []).reduce((s, p) => s + (p.amount || 0), 0), 0);
                const thisMonth = new Date().toISOString().slice(0, 7);
                const monthRevenue = accounts.reduce((sum, a) => sum + (a.payments || []).filter(p => (p.date || '').startsWith(thisMonth)).reduce((s, p) => s + (p.amount || 0), 0), 0);
                const activeSubscriptions = accounts.filter(a => a.subscriptionEnd && new Date(a.subscriptionEnd) > new Date()).length;
                // Online now = approved agencies active within the last 5 minutes.
                const onlineCount = approved.filter(r => {
                  const ts = r.data?.lastActiveAt ? new Date(r.data.lastActiveAt).getTime() : 0;
                  return ts && (nowTick - ts) < ONLINE_WINDOW_MS;
                }).length;
                const statCards = [
                  { label: 'AGENCES ACTIVES',    value: approved.length,                              icon: '🏢', grad: 'linear-gradient(135deg,#6366f1,#8b5cf6)' },
                  { label: 'ONLINE AGENCIES NOW', value: onlineCount,                                 icon: onlineCount > 0 ? '🟢' : '⚫', grad: onlineCount > 0 ? 'linear-gradient(135deg,#10b981,#22c55e)' : 'linear-gradient(135deg,#64748b,#475569)' },
                  { label: 'REVENUS TOTAL',       value: totalRevenue.toLocaleString() + ' ' + getCurrency(), icon: '💰', grad: 'linear-gradient(135deg,#10b981,#059669)' },
                  { label: 'CE MOIS',             value: monthRevenue.toLocaleString() + ' ' + getCurrency(), icon: '📅', grad: 'linear-gradient(135deg,#f59e0b,#d97706)' },
                  { label: 'ABONNEMENTS ACTIFS',  value: activeSubscriptions,                          icon: '✅', grad: 'linear-gradient(135deg,#3b82f6,#06b6d4)' },
                ];
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    {statCards.map(s => (
                      <div key={s.label} style={{ background: s.grad, borderRadius: '16px', padding: '20px 24px', display: 'flex', alignItems: 'center', gap: '16px', boxShadow: '0 4px 20px rgba(99,102,241,0.2)' }}>
                        <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', flexShrink: 0 }}>
                          {s.icon}
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.08em', marginBottom: '4px' }}>{s.label}</div>
                          <div style={{ fontSize: s.value.toString().length > 8 ? '20px' : '28px', fontWeight: 900, color: 'white', lineHeight: 1 }}>{s.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}

              {/* Online Agencies Now — live list (top 10) */}
              {(() => {
                const online = approved
                  .map(r => ({ r, ts: r.data?.lastActiveAt ? new Date(r.data.lastActiveAt).getTime() : 0 }))
                  .filter(x => x.ts && (nowTick - x.ts) < ONLINE_WINDOW_MS)
                  .sort((a, b) => b.ts - a.ts)
                  .slice(0, 10);
                const fmtAgo = (ts) => {
                  const s = Math.max(0, Math.round((nowTick - ts) / 1000));
                  if (s < 60) return `${s}s`;
                  return `${Math.round(s / 60)} min`;
                };
                return (
                  <div style={{ background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '18px', marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800, fontSize: '15px', color: T.text }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: online.length ? '#22c55e' : '#94a3b8', boxShadow: online.length ? '0 0 0 4px rgba(34,197,94,0.2)' : 'none', display: 'inline-block' }} />
                        Online Agencies Now ({online.length})
                      </div>
                      <span style={{ fontSize: '11px', color: T.textMuted }}>⟳ {dm ? '' : ''}auto 30s</span>
                    </div>
                    {online.length === 0 ? (
                      <div style={{ fontSize: '13px', color: T.textMuted, padding: '8px 0' }}>Aucune agence en ligne actuellement.</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {online.map(({ r, ts }) => (
                          <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '8px 12px', background: dm ? '#0f172a' : '#f8fafc', borderRadius: '10px', border: `1px solid ${T.cardBorder}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                              <span style={{ fontWeight: 700, fontSize: '13px', color: T.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.agencyName || r.name || r.email}</span>
                            </div>
                            <span style={{ fontSize: '11px', color: T.textMuted, flexShrink: 0 }}>il y a {fmtAgo(ts)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '20px', alignItems: 'start' }} className="grid-responsive">
                <style dangerouslySetInnerHTML={{ __html: `
                  @media (max-width: 900px) {
                    .grid-responsive { grid-template-columns: 1fr !important; }
                  }
                `}} />

                {/* Left Side: Chart & Approvals */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {/* Registrations Chart */}
                  <div style={{ background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '20px' }}>
                    <div style={{ fontWeight: 800, fontSize: '15px', color: T.text, marginBottom: '16px' }}>📈 Inscriptions — 7 derniers jours</div>
                    {(() => {
                      const days = Array.from({ length: 7 }, (_, i) => {
                        const d = new Date(); 
                        d.setDate(d.getDate() - (6 - i));
                        return d.toISOString().split('T')[0];
                      });
                      const counts = days.map(day => registrations.filter(r => (r.createdAt || '').startsWith(day)).length);
                      const max = Math.max(...counts, 1);
                      const W = 560, H = 120, pad = 30;
                      const pts = counts.map((c, i) => {
                        const x = pad + (i / 6) * (W - pad * 2);
                        const y = H - pad - (c / max) * (H - pad * 2);
                        return [x, y];
                      });
                      const polyline = pts.map(p => p.join(',')).join(' ');
                      const area = `M${pts[0][0]},${H - pad} ` + pts.map(p => `L${p[0]},${p[1]}`).join(' ') + ` L${pts[pts.length-1][0]},${H - pad} Z`;
                      return (
                        <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '120px' }}>
                          <defs>
                            <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                              <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          {[0.25, 0.5, 0.75, 1].map(f => (
                            <line key={f} x1={pad} y1={H - pad - f * (H - pad * 2)} x2={W - pad} y2={H - pad - f * (H - pad * 2)} stroke={dm ? '#334155' : '#e2e8f0'} strokeWidth={1} strokeDasharray="4" />
                          ))}
                          <path d={area} fill="url(#chartGrad)" />
                          <polyline points={polyline} fill="none" stroke="#6366f1" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                          {pts.map((p, i) => (
                            <circle key={i} cx={p[0]} cy={p[1]} r={4} fill="#6366f1" stroke="white" strokeWidth={2} />
                          ))}
                          {days.map((d, i) => {
                            const x = pad + (i / 6) * (W - pad * 2);
                            const label = ['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'][new Date(d).getDay() === 0 ? 6 : new Date(d).getDay() - 1] || d.slice(8);
                            return (
                              <text key={i} x={x} y={H - 4} textAnchor="middle" fontSize={10} fill={dm ? '#64748b' : '#94a3b8'}>{label}</text>
                            );
                          })}
                        </svg>
                      );
                    })()}
                  </div>

                  {/* Pending Approvals */}
                  {pendingReg.length > 0 && (
                    <div style={{ background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '20px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                        <div style={{ fontWeight: 800, fontSize: '15px', color: T.text }}>⏳ En attente d'approbation</div>
                        <button onClick={() => setTab('registrations')} style={{ fontSize: '12px', color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Voir tout →</button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {pendingReg.slice(0, 4).map(reg => (
                          <div key={reg.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '10px 14px', background: dm ? '#1e293b' : '#f8fafc', borderRadius: '10px', border: `1px solid ${T.cardBorder}` }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: T.text }}>{reg.agencyName || reg.name || reg.email}</div>
                              <div style={{ fontSize: '11px', color: T.textMuted }}>{reg.email} {reg.createdAt ? ' • ' + new Date(reg.createdAt).toLocaleDateString('fr-FR') : ''}</div>
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button 
                                onClick={async () => { 
                                  await supabase.from('user_registrations').update({ status: 'approved' }).eq('id', reg.id); 
                                  confirmUserEmail(reg.uid); 
                                  setRegistrations(p => p.map(r => r.id === reg.id ? {...r, status: 'approved'} : r)); 
                                }} 
                                style={{ padding: '5px 12px', borderRadius: '7px', background: '#22c55e', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                              >
                                ✅
                              </button>
                              <button 
                                onClick={async () => { 
                                  if (!confirm('Refuser?')) return; 
                                  await supabase.from('user_registrations').update({ status: 'rejected' }).eq('id', reg.id); 
                                  setRegistrations(p => p.map(r => r.id === reg.id ? {...r, status: 'rejected'} : r)); 
                                }} 
                                style={{ padding: '5px 12px', borderRadius: '7px', background: '#ef4444', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                              >
                                ❌
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Right Side: About Info */}
                <div style={{ background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '20px' }}>
                  <div style={{ fontWeight: 800, fontSize: '15px', color: T.text, marginBottom: '16px' }}>About</div>
                  {[
                    { label: 'Product',  value: 'CAR TRACK APP', color: '#6366f1' },
                    { label: 'Version',  value: '1.0.0',         color: '#3b82f6' },
                    { label: 'Release',  value: 'STABLE',        color: '#10b981' },
                    { label: 'Database', value: 'Supabase',      color: '#f59e0b' },
                    { label: 'Agences',  value: String(approved.length), color: '#8b5cf6' },
                    { label: 'En attente', value: String(pendingReg.length), color: '#ef4444' },
                  ].map(row => (
                    <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${T.divider}` }}>
                      <span style={{ fontSize: '13px', color: T.textSub, fontWeight: 600 }}>{row.label}</span>
                      <span style={{ fontSize: '12px', fontWeight: 800, background: row.color + '20', color: row.color, padding: '3px 10px', borderRadius: '6px' }}>{row.value}</span>
                    </div>
                  ))}
                  <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button onClick={() => setTab('agencies')} style={{ width: '100%', padding: '9px', borderRadius: '9px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>🏢 Gérer les agences</button>
                    <button onClick={() => setTab('registrations')} style={{ width: '100%', padding: '9px', borderRadius: '9px', background: dm ? '#1e293b' : '#f1f5f9', color: T.text, border: `1px solid ${T.cardBorder}`, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>
                      📋 Inscriptions {pendingReg.length ? ` (${pendingReg.length})` : ''}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB: AGENCIES */}
          {tab === 'agencies' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>🏢 Agences ({approved.length})</h2>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <input 
                    type="text" 
                    value={search} 
                    onChange={e => setSearch(e.target.value)}
                    placeholder="🔍 Rechercher..."
                    style={{ padding: '10px 16px', borderRadius: '10px', border: `1px solid ${T.inputBorder}`, background: T.input, color: T.text, fontSize: '13px', outline: 'none', width: '220px' }}
                  />
                  <button 
                    onClick={() => setShowRegisterAgency(true)}
                    style={{ padding: '10px 18px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 800, fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    ➕ Nouvelle agence
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {approved
                  .filter(r => !search.trim() || (r.name||r.email||'').toLowerCase().includes(search.toLowerCase()) || (r.agencyName||'').toLowerCase().includes(search.toLowerCase()))
                  .map(reg => {
                    const isExpanded = expandedReg === reg.id;
                    const currentMods = regModules[reg.id] !== undefined ? regModules[reg.id] : new Set(reg.enabledModules || [...defaultMods]);
                    const currentApis = regApis[reg.id] !== undefined ? regApis[reg.id] : new Set(reg.enabledApis || [...defaultApis]);
                    return (
                      <div key={reg.id} style={{ background: T.card, borderRadius: '14px', border: `1px solid ${T.cardBorder}`, overflow: 'hidden' }}>
                        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'linear-gradient(135deg,#3b82f6,#6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>🏢</div>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: '15px', color: T.text }}>{reg.agencyName || reg.name || reg.email}</div>
                              <div style={{ fontSize: '12px', color: T.textSub }}>{reg.email}</div>
                              {reg.phone && <div style={{ fontSize: '11px', color: T.textMuted }}>📞 {reg.phone}</div>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ padding: '3px 10px', borderRadius: '99px', fontSize: '11px', fontWeight: 700, background: reg.status === 'approved' ? '#f0fdf4' : '#fee2e2', color: reg.status === 'approved' ? '#166534' : '#991b1b', border: `1px solid ${reg.status === 'approved' ? '#bbf7d0' : '#fca5a5'}` }}>
                              {reg.status === 'approved' ? '✅ Actif' : '🚫 Suspendu'}
                            </span>
                            <button 
                              onClick={() => setEditAgency(reg)}
                              style={{ padding: '6px 12px', borderRadius: '8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                            >
                              ✏️ Modifier
                            </button>
                            <button 
                              onClick={() => setExpandedReg(isExpanded ? null : reg.id)}
                              style={{ padding: '6px 12px', borderRadius: '8px', background: isExpanded ? '#eff6ff' : T.hover, color: '#3b82f6', border: `1px solid #bfdbfe`, fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                            >
                              {isExpanded ? '🔼 Fermer' : '⚙️ Config'}
                            </button>
                            <button 
                              onClick={() => setImpersonating(reg)}
                              style={{ padding: '6px 12px', borderRadius: '8px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                            >
                              👥 Impersoner
                            </button>
                            <button 
                              onClick={() => setShowRenew(accounts.find(a => a.uid === reg.uid))}
                              style={{ padding: '6px 12px', borderRadius: '8px', background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                            >
                              🔄 Renouveler
                            </button>
                            <button 
                              onClick={async () => { 
                                if (reg.status === 'approved') {
                                  if (!confirm('Suspendre cette agence ?')) return; 
                                  await supabase.from('user_registrations').update({ status: 'rejected' }).eq('id', reg.id); 
                                  setRegistrations(p => p.map(r => r.id === reg.id ? {...r, status: 'rejected'} : r)); 
                                } else {
                                  if (!confirm('Réactiver cette agence ?')) return;
                                  await supabase.from('user_registrations').update({ status: 'approved' }).eq('id', reg.id);
                                  const updatedReg = {...reg, status: 'approved'};
                                  setRegistrations(p => p.map(r => r.id === reg.id ? updatedReg : r));
                                  // Open the edit popup to set the subscription duration (months).
                                  setEditAgency(updatedReg);
                                }
                              }}
                              style={{ padding: '6px 12px', borderRadius: '8px', background: reg.status === 'approved' ? '#fee2e2' : '#dcfce7', color: reg.status === 'approved' ? '#991b1b' : '#15803d', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                            >
                              {reg.status === 'approved' ? '🚫 Suspendre' : '🔓 Activer'}
                            </button>
                            <button 
                              onClick={() => deleteAgency(reg.uid)}
                              style={{ padding: '6px 12px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fee2e2', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                            >
                              🗑️ Supprimer
                            </button>
                          </div>
                        </div>
                        {isExpanded && (
                          <div style={{ borderTop: `1px solid ${T.divider}`, padding: '16px 20px', background: dm ? '#0f172a' : '#f8fafc' }}>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: T.textSub, marginBottom: '12px' }}>⚙️ Modules activés</div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px, 1fr))', gap: '8px', marginBottom: '14px' }}>
                              {ALL_MODULES.filter(m => !isHiddenModule(m.id)).map(mod => {
                                const enabled = currentMods.has(mod.id);
                                return (
                                  <label key={mod.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', background: enabled ? '#eff6ff' : T.card, border: `1px solid ${enabled ? '#93c5fd' : T.cardBorder}` }}>
                                    <input 
                                      type="checkbox" 
                                      checked={enabled} 
                                      onChange={() => { 
                                        const n = new Set(currentMods); 
                                        if (enabled) n.delete(mod.id); else n.add(mod.id); 
                                        setRegModules(p => ({...p, [reg.id]: n})); 
                                      }} 
                                      style={{ accentColor: '#3b82f6' }} 
                                    />
                                    <span style={{ fontSize: '12px', fontWeight: 600, color: enabled ? '#1d4ed8' : T.textSub }}>{mod.emoji} {mod.label}</span>
                                  </label>
                                );
                              })}
                            </div>
                            <div style={{ fontWeight: 700, fontSize: '13px', color: T.textSub, marginBottom: '10px', borderTop: `1px solid ${T.divider}`, paddingTop: '12px' }}>🔌 APIs</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '14px' }}>
                              {ALL_APIS.map(api => {
                                const on = currentApis.has(api.id);
                                return (
                                  <label key={api.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', background: on ? '#fefce8' : T.card, border: `1px solid ${on ? '#fde047' : T.cardBorder}` }}>
                                    <input 
                                      type="checkbox" 
                                      checked={on} 
                                      onChange={() => { 
                                        const n = new Set(currentApis); 
                                        if (on) n.delete(api.id); else n.add(api.id); 
                                        setRegApis(p => ({...p, [reg.id]: n})); 
                                      }} 
                                      style={{ accentColor: '#ca8a04' }} 
                                    />
                                    <div>
                                      <div style={{ fontSize: '12px', fontWeight: 700, color: on ? '#854d0e' : T.textSub }}>{api.emoji} {api.label}</div>
                                      <div style={{ fontSize: '10px', color: T.textMuted }}>{api.desc}</div>
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                            <button
                              disabled={savingModules === reg.id}
                              onClick={async () => {
                                setSavingModules(reg.id);
                                try {
                                  await supabase.from('user_registrations').update({ enabled_modules: [...currentMods], enabled_apis: [...currentApis] }).eq('id', reg.id);
                                  setExpandedReg(null);
                                } catch (e) { 
                                  alert('Erreur: ' + e.message); 
                                }
                                setSavingModules(null);
                              }}
                              style={{ padding: '8px 18px', borderRadius: '8px', background: '#3b82f6', color: 'white', border: 'none', fontWeight: 700, fontSize: '13px', cursor: 'pointer', opacity: savingModules === reg.id ? 0.7 : 1 }}
                            >
                              {savingModules === reg.id ? '⏳...' : '💾 Sauvegarder'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* TAB: REGISTRATIONS */}
          {tab === 'registrations' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>📋 Inscriptions</h2>
                <div style={{ display: 'flex', background: T.hover, borderRadius: '10px', padding: '4px', gap: '2px' }}>
                  {[
                    ['pending', '⏳ En attente', pendingReg.length],
                    ['approved', '✅ Approuvés', approved.length],
                    ['rejected', '❌ Refusés', rejected.length],
                    ['all', 'Tous', registrations.length]
                  ].map(([id, lbl, cnt]) => (
                    <button 
                      key={id} 
                      onClick={() => setRegFilter(id)}
                      style={{ 
                        padding: '6px 12px', borderRadius: '8px', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer', 
                        background: regFilter === id ? 'white' : 'transparent', color: regFilter === id ? '#1e293b' : T.textSub, 
                        boxShadow: regFilter === id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' 
                      }}
                    >
                      {lbl} <span style={{ background: regFilter === id ? '#3b82f6' : T.cardBorder, color: regFilter === id ? 'white' : T.textSub, borderRadius: '99px', fontSize: '10px', padding: '1px 6px', marginLeft: '4px' }}>{cnt}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Registrations List */}
              {(() => {
                const list = registrations
                  .filter(r => regFilter === 'all' || r.status === regFilter)
                  .sort((a, b) => (b.createdAt||'').localeCompare(a.createdAt||''));
                if (list.length === 0) return <div style={{ textAlign: 'center', padding: '60px', color: T.textMuted }}>📭 Aucune inscription</div>;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {list.map(reg => {
                      const isExpanded = expandedReg === reg.id;
                      const currentMods = regModules[reg.id] !== undefined ? regModules[reg.id] : new Set(reg.enabledModules || [...defaultMods]);
                      const currentApis = regApis[reg.id] !== undefined ? regApis[reg.id] : new Set(reg.enabledApis || [...defaultApis]);
                      const statusColor = reg.status === 'approved' ? '#10b981' : reg.status === 'rejected' ? '#ef4444' : '#f59e0b';
                      const statusBg    = reg.status === 'approved' ? '#f0fdf4' : reg.status === 'rejected' ? '#fef2f2' : '#fffbeb';
                      const statusLabel = reg.status === 'approved' ? '✅ Approuvé' : reg.status === 'rejected' ? '❌ Refusé' : '⏳ En attente';
                      return (
                        <div key={reg.id} style={{ background: T.card, borderRadius: '14px', border: `1px solid ${T.cardBorder}`, overflow: 'hidden' }}>
                          <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                            <div>
                              <div style={{ fontWeight: 800, fontSize: '15px', color: T.text }}>{reg.agencyName || reg.name || reg.email}</div>
                              <div style={{ fontSize: '12px', color: T.textSub, marginTop: '2px' }}>{reg.email} {reg.phone ? ' • 📞 ' + reg.phone : ''}</div>
                              <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>{reg.createdAt ? '🕐 ' + new Date(reg.createdAt).toLocaleString('fr-FR') : ''}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: 700, background: statusBg, color: statusColor, border: `1px solid ${statusColor}40` }}>{statusLabel}</span>
                              <button onClick={() => setExpandedReg(isExpanded ? null : reg.id)} style={{ padding: '6px 10px', borderRadius: '8px', background: T.hover, color: '#3b82f6', border: `1px solid #bfdbfe`, fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>⚙️</button>
                              
                              {reg.status === 'pending' && (
                                <button 
                                  onClick={async () => {
                                    const allMods = ALL_MODULES.map(m => m.id);
                                    const allApis = ALL_APIS.map(a => a.id);
                                    const updateData = {
                                      status: 'approved',
                                      enabled_modules: reg.enabledModules || allMods,
                                      enabled_apis: reg.enabledApis || allApis
                                    };
                                    await supabase.from('user_registrations').update(updateData).eq('id', reg.id);
                                    confirmUserEmail(reg.uid);
                                    const updatedReg = {...reg, status: 'approved', enabledModules: updateData.enabled_modules, enabledApis: updateData.enabled_apis};
                                    setRegistrations(p => p.map(r => r.id === reg.id ? updatedReg : r));
                                    // Open the edit popup so the SA sets the subscription duration (months).
                                    setEditAgency(updatedReg);
                                  }}
                                  style={{ padding: '6px 14px', borderRadius: '8px', background: '#22c55e', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                >
                                  ✅ Approuver
                                </button>
                              )}
                              {reg.status === 'rejected' && (
                                <button 
                                  onClick={async () => { 
                                    if (!confirm('Réactiver ce compte ? Toutes les données seront restaurées.')) return; 
                                    await supabase.from('user_registrations').update({ status: 'approved' }).eq('id', reg.id); 
                                    confirmUserEmail(reg.uid); 
                                    setRegistrations(p => p.map(r => r.id === reg.id ? {...r, status: 'approved'} : r)); 
                                  }}
                                  style={{ padding: '6px 14px', borderRadius: '8px', background: '#3b82f6', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                >
                                  🔓 Réactiver
                                </button>
                              )}
                              {reg.status !== 'rejected' && (
                                <button 
                                  onClick={async () => { 
                                    if (!confirm('Suspendre ce compte ?')) return; 
                                    await supabase.from('user_registrations').update({ status: 'rejected' }).eq('id', reg.id); 
                                    setRegistrations(p => p.map(r => r.id === reg.id ? {...r, status: 'rejected'} : r)); 
                                  }}
                                  style={{ padding: '6px 12px', borderRadius: '8px', background: '#ef4444', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                                >
                                  🚫 Suspendre
                                </button>
                              )}
                            </div>
                          </div>
                          {isExpanded && (
                            <div style={{ borderTop: `1px solid ${T.divider}`, padding: '14px 20px', background: dm ? '#0f172a' : '#f8fafc' }}>
                              <div style={{ fontWeight: 700, fontSize: '12px', color: T.textSub, marginBottom: '10px' }}>⚙️ Modules</div>
                              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(145px,1fr))', gap: '6px', marginBottom: '12px' }}>
                                {ALL_MODULES.filter(m => !isHiddenModule(m.id)).map(mod => {
                                  const enabled = currentMods.has(mod.id);
                                  return (
                                    <label key={mod.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '8px', cursor: 'pointer', background: enabled ? '#eff6ff' : T.card, border: `1px solid ${enabled ? '#93c5fd' : T.cardBorder}`, fontSize: '12px' }}>
                                      <input 
                                        type="checkbox" 
                                        checked={enabled} 
                                        onChange={() => { 
                                          const n = new Set(currentMods); 
                                          if (enabled) n.delete(mod.id); else n.add(mod.id); 
                                          setRegModules(p => ({...p, [reg.id]: n})); 
                                        }} 
                                      />
                                      <span style={{ fontWeight: 600, color: enabled ? '#1d4ed8' : T.textSub }}>{mod.emoji} {mod.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                              <div style={{ fontWeight: 700, fontSize: '12px', color: T.textSub, marginBottom: '8px' }}>🔌 APIs</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                                {ALL_APIS.map(api => {
                                  const on = currentApis.has(api.id);
                                  return (
                                    <label key={api.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '8px', cursor: 'pointer', background: on ? '#fefce8' : T.card, border: `1px solid ${on ? '#fde047' : T.cardBorder}`, fontSize: '12px' }}>
                                      <input 
                                        type="checkbox" 
                                        checked={on} 
                                        onChange={() => { 
                                          const n = new Set(currentApis); 
                                          if (on) n.delete(api.id); else n.add(api.id); 
                                          setRegApis(p => ({...p, [reg.id]: n})); 
                                        }} 
                                      />
                                      <span style={{ fontWeight: 600, color: on ? '#854d0e' : T.textSub }}>{api.emoji} {api.label}</span>
                                    </label>
                                  );
                                })}
                              </div>
                              <button
                                disabled={savingModules === reg.id}
                                onClick={async () => { 
                                  setSavingModules(reg.id); 
                                  try { 
                                    await supabase.from('user_registrations').update({ enabled_modules: [...currentMods], enabled_apis: [...currentApis] }).eq('id', reg.id); 
                                    setExpandedReg(null); 
                                  } catch (e) { 
                                    alert(e.message); 
                                  } 
                                  setSavingModules(null); 
                                }}
                                style={{ padding: '7px 16px', borderRadius: '8px', background: '#3b82f6', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                              >
                                {savingModules === reg.id ? '⏳...' : '💾 Sauvegarder'}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* TAB: SUBSCRIPTIONS */}
          {tab === 'subscriptions' && (
            <SASubscriptionsTab 
              T={T} 
              dm={dm} 
              plans={plans} 
              setPlans={setPlans} 
              savePlanPrices={savePlanPrices} 
              savingPlan={savingPlan} 
              accounts={accounts} 
            />
          )}

          {/* TAB: USERS & STAFF */}
          {tab === 'users_staff' && (
            <SAUsersStaffTab 
              T={T} 
              dm={dm} 
              registrations={registrations} 
              accounts={accounts}
              deleteAgency={deleteAgency}
              setEditAgency={setEditAgency}
              setImpersonating={setImpersonating}
              setShowRenew={setShowRenew}
              plans={plans}
              __SA_TOKEN__={__SA_TOKEN__}
            />
          )}

          {/* TAB: TELEGRAM BROADCAST (Super Admin only) */}
          {tab === 'blacklist' && (
            <BlacklistManager T={T} dm={dm} saActor={currentCoAdmin ? `co-admin:${currentCoAdmin}` : 'super-admin'} />
          )}

          {tab === 'blacklist_approval' && (
            <BlacklistApprovalCenter T={T} dm={dm} saActor={currentCoAdmin ? `co-admin:${currentCoAdmin}` : 'super-admin'} />
          )}

          {tab === 'referrals' && (() => {
            const rows = buildReferralRows(accounts);
            const pending = rows.filter(r => r.status === 'pending').length;
            const activated = rows.filter(r => r.status === 'activated').length;
            const totalFreeDays = accounts.reduce((s, a) => s + (a.referralFreeDaysTotal || 0), 0);
            const copy = (txt) => { try { navigator.clipboard.writeText(txt); } catch (_) {} };
            return (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>🎁 Système de Parrainage</h2>
                  <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '4px' }}>
                    Chaque agence a un lien unique. La récompense est créditée au parrain après le 1er paiement du filleul (une seule fois).
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
                  {[
                    { label: 'Parrainages en attente', value: pending, color: '#f59e0b' },
                    { label: 'Parrainages activés', value: activated, color: '#10b981' },
                    { label: 'Jours offerts (total)', value: totalFreeDays, color: '#6366f1' },
                  ].map(s => (
                    <div key={s.label} style={{ flex: '1 1 150px', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '14px', padding: '14px 18px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 900, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '12px', color: T.textSub, fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                  <div style={{ flex: '1 1 340px', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '14px', padding: '14px 18px' }}>
                    <div style={{ fontSize: '12px', color: T.textSub, fontWeight: 700, marginBottom: '6px' }}>Récompense par parrainage (le filleul choisit)</div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: T.textSub }}>Jours:</span>
                      <input type="number" min={0} max={365} value={referralFreeDays}
                        onChange={e => setReferralFreeDays(parseInt(e.target.value, 10) || 0)}
                        style={{ width: '64px', padding: '6px 8px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }} />
                      <span style={{ fontSize: '12px', color: T.textSub }}>OU Montant:</span>
                      <input type="number" min={0} value={referralCash}
                        onChange={e => setReferralCash(parseInt(e.target.value, 10) || 0)}
                        style={{ width: '74px', padding: '6px 8px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }} />
                      <input type="text" value={referralCurrency}
                        onChange={e => setReferralCurrency(e.target.value)}
                        style={{ width: '50px', padding: '6px 8px', borderRadius: '8px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text }} />
                      <button onClick={saveReferralDays}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#6366f1', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer' }}>
                        {referralSaved ? '✅' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: T.text, margin: '0 0 8px' }}>Suivi des parrainages</h3>
                <div style={{ overflowX: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '14px', marginBottom: '22px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: dm ? '#1e293b' : '#f8fafc', color: T.textSub, textAlign: 'left' }}>
                        {['Parrain', 'Filleul', 'Statut', 'Date'].map(h => <th key={h} style={{ padding: '10px 14px', fontWeight: 800 }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr><td colSpan={4} style={{ padding: '16px', color: T.textSub, textAlign: 'center' }}>Aucun parrainage pour le moment.</td></tr>
                      ) : rows.map((r, i) => (
                        <tr key={i} style={{ borderTop: `1px solid ${T.cardBorder}`, color: T.text }}>
                          <td style={{ padding: '10px 14px', fontWeight: 700 }}>{r.referrerName}</td>
                          <td style={{ padding: '10px 14px' }}>{r.referredName}</td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 800,
                              background: r.status === 'activated' ? '#dcfce7' : '#fef3c7', color: r.status === 'activated' ? '#15803d' : '#b45309' }}>
                              {r.status === 'activated' ? '✅ Activé' : '⏳ En attente'}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>{r.date ? new Date(r.date).toLocaleDateString('fr-FR') : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <h3 style={{ fontSize: '15px', fontWeight: 800, color: T.text, margin: '0 0 8px' }}>Liens de parrainage par agence</h3>
                <div style={{ overflowX: 'auto', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '14px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: dm ? '#1e293b' : '#f8fafc', color: T.textSub, textAlign: 'left' }}>
                        {['Agence', 'Lien', 'Filleuls', 'Jours gagnés', ''].map(h => <th key={h} style={{ padding: '10px 14px', fontWeight: 800 }}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((a) => {
                        const link = referralLink(a.uid);
                        const filleuls = accounts.filter(x => x.referredBy === a.uid).length;
                        return (
                          <tr key={a.uid} style={{ borderTop: `1px solid ${T.cardBorder}`, color: T.text }}>
                            <td style={{ padding: '10px 14px', fontWeight: 700 }}>{a.agencyName || a.name || a.email}</td>
                            <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: '11px', color: T.textSub, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</td>
                            <td style={{ padding: '10px 14px' }}>{filleuls}</td>
                            <td style={{ padding: '10px 14px' }}>{a.referralFreeDaysTotal || 0}</td>
                            <td style={{ padding: '10px 14px' }}>
                              <button onClick={() => copy(link)} style={{ padding: '4px 10px', borderRadius: '8px', background: '#6366f1', color: '#fff', border: 'none', fontWeight: 700, cursor: 'pointer', fontSize: '12px' }}>📋 Copier</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {tab === 'broadcast' && !currentCoAdmin && (() => {
            const withChat = approved.filter(r => r.data?.telegramChatId).length;
            const withoutChat = approved.length - withChat;
            return (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>📢 Telegram Broadcast</h2>
                  <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '4px' }}>
                    Envoyez un message à toutes les agences actives via le bot Telegram.
                  </div>
                </div>

                {/* Recipient summary */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
                  {[
                    { label: 'Agences actives', value: approved.length, color: '#3b82f6' },
                    { label: 'Avec Telegram', value: withChat, color: '#10b981' },
                    { label: 'Sans Telegram (ignorées)', value: withoutChat, color: '#f59e0b' },
                  ].map(s => (
                    <div key={s.label} style={{ flex: '1 1 150px', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '14px', padding: '14px 18px' }}>
                      <div style={{ fontSize: '24px', fontWeight: 900, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: '12px', color: T.textSub, fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Message editor */}
                <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '16px', padding: '20px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 700, color: T.textSub, display: 'block', marginBottom: '8px' }}>
                    Message
                  </label>
                  <textarea
                    value={broadcastMsg}
                    onChange={e => setBroadcastMsg(e.target.value)}
                    maxLength={4000}
                    placeholder="Écrivez votre message ici… (il sera envoyé tel quel à chaque agence)"
                    style={{
                      width: '100%', minHeight: '200px', resize: 'vertical', boxSizing: 'border-box',
                      padding: '14px 16px', borderRadius: '12px', border: `2px solid ${T.inputBorder}`,
                      background: T.input, color: T.text, fontSize: '14px', lineHeight: 1.6, outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', flexWrap: 'wrap', gap: '10px' }}>
                    <span style={{ fontSize: '12px', color: T.textMuted }}>{broadcastMsg.length}/4000</span>
                    <button
                      onClick={sendBroadcast}
                      disabled={broadcastBusy || !broadcastMsg.trim()}
                      style={{
                        padding: '12px 22px', borderRadius: '12px', border: 'none',
                        background: (broadcastBusy || !broadcastMsg.trim()) ? '#94a3b8' : 'linear-gradient(135deg,#3b82f6,#6366f1)',
                        color: 'white', fontWeight: 800, fontSize: '14px',
                        cursor: (broadcastBusy || !broadcastMsg.trim()) ? 'not-allowed' : 'pointer',
                        boxShadow: '0 4px 15px rgba(59,130,246,0.35)',
                      }}
                    >
                      {broadcastBusy ? '⏳ Envoi en cours…' : '📤 Send to All Agents'}
                    </button>
                  </div>
                </div>

                {/* Results */}
                {broadcastResult && (
                  <div style={{ marginTop: '18px', background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: '16px', padding: '20px' }}>
                    <div style={{ fontWeight: 800, fontSize: '15px', color: T.text, marginBottom: '14px' }}>✅ Résultat de l'envoi</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px,1fr))', gap: '12px' }}>
                      {[
                        { label: 'Total agences', value: broadcastResult.total, color: '#3b82f6' },
                        { label: 'Envoyés', value: broadcastResult.sent, color: '#10b981' },
                        { label: 'Échoués', value: broadcastResult.failed, color: '#ef4444' },
                        { label: 'Ignorés (sans Telegram)', value: broadcastResult.skipped, color: '#f59e0b' },
                      ].map(s => (
                        <div key={s.label} style={{ background: s.color + '15', border: `1px solid ${s.color}40`, borderRadius: '12px', padding: '14px' }}>
                          <div style={{ fontSize: '26px', fontWeight: 900, color: s.color }}>{s.value}</div>
                          <div style={{ fontSize: '11px', color: T.textSub, fontWeight: 600 }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                    {Array.isArray(broadcastResult.failures) && broadcastResult.failures.length > 0 && (
                      <div style={{ marginTop: '14px', fontSize: '12px', color: T.textSub }}>
                        <strong style={{ color: '#ef4444' }}>Échecs:</strong> {broadcastResult.failures.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

          {/* TAB: SETTINGS */}
          {tab === 'settings' && (
            <div>
              {!currentCoAdmin && <SACoAdminsPanel T={T} dm={dm} coAdmins={coAdmins} saveCoAdmins={saveCoAdmins} />}
              {!currentCoAdmin && <SADefaultModulesPanel T={T} dm={dm} defaultMods={defaultMods} defaultApis={defaultApis} saveDefaultModules={saveDefaultModules} />}
              <SAGlobalConfigPanel T={T} dm={dm} />
            </div>
          )}
        </div>
      </div>

      {/* MODALS */}
      {showRegisterAgency && (
        <SARegisterAgencyModal 
          onClose={() => setShowRegisterAgency(false)}
          onCreated={() => { setShowRegisterAgency(false); if (fetchRegsRef.current) fetchRegsRef.current(); }}
          defaultMods={[...defaultMods]}
          defaultApis={[...defaultApis]}
        />
      )}

      {editAgency && (
        <SAAgencyEditModal 
          reg={editAgency}
          plans={plans}
          onClose={() => { setEditAgency(null); if (fetchRegsRef.current) fetchRegsRef.current(); }}
        />
      )}

      {showRenew && (
        <SARenewModal 
          account={showRenew} 
          plans={plans}
          onClose={() => { setShowRenew(null); if (fetchRegsRef.current) fetchRegsRef.current(); }}
        />
      )}
    </div>
  );
}

// ============================================
// SA: Register New Agency Modal
// ============================================
function SARegisterAgencyModal({ onClose, onCreated, defaultMods, defaultApis }) {
  const [data, setData] = useState({ agencyName: '', email: '', password: '', phone: '', city: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const inp = (label, key, type = 'text', placeholder = '') => (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px', color: '#475569' }}>{label}</label>
      <input
        type={type} 
        value={data[key]} 
        placeholder={placeholder}
        onChange={e => set(key, e.target.value)}
        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
      />
    </div>
  );

  const create = async () => {
    if (!data.agencyName.trim()) return setError('Nom de l\'agence requis');
    if (!data.email.trim()) return setError('Email requis');
    if (data.password.length < 6) return setError('Mot de passe min 6 caractères');
    setSaving(true);
    setError('');
    try {
      // 1. Create Supabase auth user via server endpoint
      const saToken = __SA_TOKEN__ || '';
      const createRes = await fetch('/api/sa/create-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: data.email.trim(), password: data.password, saToken })
      });
      const createData = await createRes.json();
      if (!createRes.ok) throw new Error(createData.error || 'Erreur création compte');
      const uid = createData.uid;
      if (!uid) throw new Error('Erreur création compte auth');

      // 2. Insert user_registrations directly as approved
      const regData = {
        uid, agencyId: uid,
        email: data.email.trim(),
        name: data.agencyName.trim(),
        agencyName: data.agencyName.trim(),
        phone: data.phone.trim(),
        city: data.city.trim(),
        createdAt: new Date().toISOString(),
        createdBySA: true
      };
      const { error: regErr } = await supabase.from('user_registrations').upsert({
        id: uid, 
        uid,
        agency_id: uid,
        status: 'approved',
        enabled_modules: (defaultMods && defaultMods.length) ? defaultMods : ALL_MODULES.map(m => m.id),
        enabled_apis: (defaultApis && defaultApis.length) ? defaultApis : ALL_APIS.map(a => a.id),
        data: regData
      });
      if (regErr) throw new Error(regErr.message);
      onCreated();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  return (
    <div 
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui,sans-serif' }}
    >
      <div style={{ background: 'white', borderRadius: '20px', padding: '28px', maxWidth: '480px', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <div style={{ fontWeight: 900, fontSize: '18px', color: '#1e293b' }}>➕ Nouvelle agence</div>
            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>Compte créé directement approuvé</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ display: 'grid', gap: '12px' }}>
          {inp('🏢 Nom de l\'agence *', 'agencyName', 'text', 'Auto Belkhir, Swayah Car...')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {inp('✉️ Email *', 'email', 'email', 'email@agence.ma')}
            {inp('🔑 Mot de passe *', 'password', 'password', 'Min 6 caractères')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {inp('📞 Téléphone', 'phone', 'text', '06...')}
            {inp('📍 Ville', 'city', 'text', 'Casablanca...')}
          </div>
        </div>
        {error && <div style={{ marginTop: '12px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#991b1b', fontSize: '13px' }}>❌ {error}</div>}
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #e2e8f0', background: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>Annuler</button>
          <button 
            onClick={create} 
            disabled={saving}
            style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: saving ? '#a5b4fc' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 700, fontSize: '14px', cursor: saving ? 'default' : 'pointer' }}
          >
            {saving ? '⏳ Création...' : '✅ Créer & Approuver'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SA: Subscriptions Tab — full plan editor + revenue
// ============================================
function SASubscriptionsTab({ T, dm, plans, setPlans, savePlanPrices, savingPlan, accounts }) {
  const cur = getCurrency();
  const now = new Date();
  const thisMonth = now.toISOString().slice(0, 7);
  const totalRevenue   = accounts.reduce((s, a) => s + (a.payments || []).reduce((x, p) => x + (p.amount || 0), 0), 0);
  const monthRevenue   = accounts.reduce((s, a) => s + (a.payments || []).filter(p => (p.date || '').startsWith(thisMonth)).reduce((x, p) => x + (p.amount || 0), 0), 0);
  const activeSubs     = accounts.filter(a => a.subscriptionEnd && new Date(a.subscriptionEnd) > now).length;
  const expiringSoon   = accounts.filter(a => { 
    const d = a.subscriptionEnd && Math.ceil((new Date(a.subscriptionEnd) - now) / 86400000); 
    return d > 0 && d <= 7; 
  }).length;

  const updatePlan = (idx, key, val) => setPlans(plans.map((p, i) => i === idx ? { ...p, [key]: val } : p));
  const addPlan = () => setPlans([...plans, { id: 'plan_' + Date.now(), label: 'Nouveau plan', months: 1, price: 0, accent: '#6366f1', badge: '', icon: '🎯' }]);
  const removePlan = (idx) => { 
    if (!confirm('Supprimer ce plan ?')) return; 
    setPlans(plans.filter((_, i) => i !== idx)); 
  };

  const statCard = (label, value, sub, color) => (
    <div style={{ background: T.card, borderRadius: '14px', border: `1px solid ${T.cardBorder}`, padding: '18px 20px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: T.textMuted, marginBottom: '6px' }}>{label}</div>
      <div style={{ fontSize: '26px', fontWeight: 900, color }}>{value}</div>
      {sub && <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '3px' }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ fontSize: '20px', fontWeight: 900, color: T.text }}>💳 Abonnements</div>
          <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '2px' }}>Revenus & gestion des plans</div>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={addPlan} style={{ padding: '9px 16px', borderRadius: '9px', border: `1px solid ${T.cardBorder}`, background: T.card, color: T.text, fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>➕ Ajouter plan</button>
          <button 
            onClick={() => savePlanPrices(plans)} 
            disabled={savingPlan}
            style={{ padding: '9px 20px', borderRadius: '9px', border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 800, fontSize: '13px', cursor: savingPlan ? 'wait' : 'pointer', opacity: savingPlan ? 0.7 : 1 }}
          >
            {savingPlan ? '⏳...' : '💾 Sauvegarder'}
          </button>
        </div>
      </div>

      {/* Revenue stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px,1fr))', gap: '12px', marginBottom: '24px' }}>
        {statCard('💰 Revenus total',    totalRevenue.toLocaleString() + ' ' + cur,  null,             '#10b981')}
        {statCard('📅 Ce mois',          monthRevenue.toLocaleString() + ' ' + cur,  thisMonth,        '#6366f1')}
        {statCard('✅ Abonnements actifs', activeSubs,                                `/ ${accounts.length} agences`, '#3b82f6')}
        {statCard('⚠️ Expire bientôt',   expiringSoon,                               '≤ 7 jours',      expiringSoon > 0 ? '#ef4444' : T.textMuted)}
      </div>

      {/* Plans editor */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {plans.map((plan, idx) => (
          <div key={plan.id} style={{ background: T.card, borderRadius: '16px', border: `2px solid ${plan.accent || '#6366f1'}30`, overflow: 'hidden' }}>
            <div style={{ height: '4px', background: plan.accent || '#6366f1' }} />
            <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: 'auto 1fr 1fr 1fr 1fr auto', gap: '12px', alignItems: 'center' }} className="plan-editor-row">
              <style dangerouslySetInnerHTML={{ __html: `
                @media (max-width: 768px) {
                  .plan-editor-row { grid-template-columns: 1fr !important; }
                }
              `}} />
              {/* Color picker */}
              <input 
                type="color" 
                value={plan.accent || '#6366f1'} 
                onChange={e => updatePlan(idx, 'accent', e.target.value)}
                style={{ width: '36px', height: '36px', borderRadius: '8px', border: 'none', cursor: 'pointer', padding: '2px' }} 
              />

              {/* Plan name */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: T.textMuted, marginBottom: '3px' }}>NOM DU PLAN</div>
                <input 
                  value={plan.label} 
                  onChange={e => updatePlan(idx, 'label', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${T.cardBorder}`, background: T.input, color: T.text, fontSize: '14px', fontWeight: 700, boxSizing: 'border-box' }} 
                />
              </div>

              {/* Months */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: T.textMuted, marginBottom: '3px' }}>DURÉE (MOIS)</div>
                <input 
                  type="number" 
                  min="1" 
                  value={plan.months} 
                  onChange={e => updatePlan(idx, 'months', parseInt(e.target.value) || 1)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${T.cardBorder}`, background: T.input, color: T.text, fontSize: '14px', fontWeight: 700, boxSizing: 'border-box' }} 
                />
              </div>

              {/* Price */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: T.textMuted, marginBottom: '3px' }}>PRIX ({cur})</div>
                <input 
                  type="number" 
                  min="0" 
                  step="10" 
                  value={plan.price} 
                  onChange={e => updatePlan(idx, 'price', parseFloat(e.target.value) || 0)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${T.cardBorder}`, background: T.input, color: plan.accent || '#6366f1', fontSize: '16px', fontWeight: 900, boxSizing: 'border-box' }} 
                />
              </div>

              {/* Badge */}
              <div>
                <div style={{ fontSize: '10px', fontWeight: 700, color: T.textMuted, marginBottom: '3px' }}>BADGE (optionnel)</div>
                <input 
                  value={plan.badge || ''} 
                  placeholder="Ex: Populaire" 
                  onChange={e => updatePlan(idx, 'badge', e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: '7px', border: `1px solid ${T.cardBorder}`, background: T.input, color: T.text, fontSize: '12px', boxSizing: 'border-box' }} 
                />
              </div>

              {/* Delete */}
              <button 
                onClick={() => removePlan(idx)}
                style={{ padding: '8px 10px', borderRadius: '8px', border: 'none', background: '#fef2f2', color: '#ef4444', fontWeight: 700, fontSize: '16px', cursor: 'pointer' }}
              >
                🗑️
              </button>
            </div>

            {/* Preview row */}
            {plan.price > 0 && (
              <div style={{ padding: '8px 20px 12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 700, color: plan.accent, background: plan.accent + '15', padding: '3px 10px', borderRadius: '99px' }}>
                  ≈ {(plan.price / plan.months).toFixed(0)} {cur}/mois
                </span>
                {plan.badge && <span style={{ fontSize: '11px', fontWeight: 700, color: 'white', background: plan.accent, padding: '3px 10px', borderRadius: '99px' }}>{plan.badge}</span>}
                <span style={{ fontSize: '11px', color: T.textMuted }}>
                  Utilisé par {accounts.filter(a => (a.subscriptionPlan || '') === plan.label).length} agence(s)
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Payment history */}
      {accounts.some(a => (a.payments || []).length > 0) && (
        <div style={{ marginTop: '24px', background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '20px' }}>
          <div style={{ fontWeight: 800, fontSize: '15px', color: T.text, marginBottom: '14px' }}>📜 Historique des paiements</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '300px', overflowY: 'auto' }}>
            {accounts.flatMap(a => (a.payments || []).map(p => ({ ...p, agency: a.agencyName || a.email || a.id })))
              .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
              .slice(0, 30)
              .map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: dm ? '#0f172a' : '#f8fafc', borderRadius: '8px', fontSize: '13px' }}>
                  <span style={{ fontWeight: 600, color: T.text }}>{p.agency}</span>
                  <span style={{ color: T.textSub }}>{p.plan}</span>
                  <span style={{ fontWeight: 800, color: '#10b981' }}>+{p.amount} {cur}</span>
                  <span style={{ color: T.textMuted, fontSize: '11px' }}>{p.date ? new Date(p.date).toLocaleDateString('fr-FR') : ''}</span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SA: Default Modules Panel
// ============================================
function SADefaultModulesPanel({ T, dm, defaultMods, defaultApis, saveDefaultModules }) {
  const [mods, setMods] = useState(new Set(defaultMods));
  const [apis, setApis] = useState(new Set(defaultApis));
  const [saving, setSaving] = useState(false);

  useEffect(() => { setMods(new Set(defaultMods)); }, [defaultMods]);
  useEffect(() => { setApis(new Set(defaultApis)); }, [defaultApis]);

  const toggleMod = (id) => setMods(p => { 
    const n = new Set(p); 
    if (n.has(id)) n.delete(id); else n.add(id); 
    return n; 
  });
  const toggleApi = (id) => setApis(p => { 
    const n = new Set(p); 
    if (n.has(id)) n.delete(id); else n.add(id); 
    return n; 
  });

  const save = async () => { 
    setSaving(true); 
    await saveDefaultModules(mods, apis); 
    setSaving(false); 
  };

  return (
    <div style={{ background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '20px', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '16px', color: T.text }}>⚙️ Modules par défaut</div>
          <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '2px' }}>Ces modules seront activés automatiquement pour toute nouvelle agence</div>
        </div>
        <button onClick={save} disabled={saving} style={{ padding: '8px 18px', borderRadius: '99px', border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving ? '⏳...' : '💾 Sauvegarder'}
        </button>
      </div>
      <div style={{ fontWeight: 700, fontSize: '12px', color: T.textSub, marginBottom: '8px' }}>📦 Modules</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(155px,1fr))', gap: '8px', marginBottom: '14px' }}>
        {ALL_MODULES.filter(m => !isHiddenModule(m.id)).map(mod => {
          const on = mods.has(mod.id);
          return (
            <label key={mod.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', background: on ? '#eff6ff' : (dm ? '#0f172a' : '#f8fafc'), border: `1px solid ${on ? '#93c5fd' : T.cardBorder}` }}>
              <input type="checkbox" checked={on} onChange={() => toggleMod(mod.id)} style={{ accentColor: '#3b82f6' }} />
              <span style={{ fontSize: '12px', fontWeight: 600, color: on ? '#1d4ed8' : T.textSub }}>{mod.emoji} {mod.label}</span>
            </label>
          );
        })}
      </div>
      <div style={{ fontWeight: 700, fontSize: '12px', color: T.textSub, marginBottom: '8px' }}>🔌 APIs</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {ALL_APIS.map(api => {
          const on = apis.has(api.id);
          return (
            <label key={api.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', background: on ? '#fefce8' : (dm ? '#0f172a' : '#f8fafc'), border: `1px solid ${on ? '#fde047' : T.cardBorder}` }}>
              <input type="checkbox" checked={on} onChange={() => toggleApi(api.id)} style={{ accentColor: '#ca8a04' }} />
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: on ? '#854d0e' : T.textSub }}>{api.emoji} {api.label}</div>
                <div style={{ fontSize: '10px', color: T.textMuted }}>{api.desc}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ============================================
// SA: Co-Admins Panel
// ============================================
function SACoAdminsPanel({ T, dm, coAdmins, saveCoAdmins }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', permissions: [] });
  const [saving, setSaving] = useState(false);

  const openAdd = () => { setForm({ name: '', email: '', password: '', permissions: [] }); setEditIdx(null); setShowAdd(true); };
  const openEdit = (idx) => { setForm({ ...coAdmins[idx] }); setEditIdx(idx); setShowAdd(true); };
  const togglePerm = (id) => setForm(f => ({ ...f, permissions: f.permissions.includes(id) ? f.permissions.filter(p => p !== id) : [...f.permissions, id] }));

  const save = async () => {
    if (!form.email || !form.password) return alert('Email et mot de passe requis');
    setSaving(true);
    try {
      const list = [...coAdmins];
      if (editIdx !== null) list[editIdx] = { ...form };
      else list.push({ ...form, id: 'ca_' + Date.now() });
      await saveCoAdmins(list);
      setShowAdd(false);
    } catch (e) { 
      alert('❌ ' + e.message); 
    }
    setSaving(false);
  };

  const remove = async (idx) => {
    if (!confirm('Supprimer ce co-admin ?')) return;
    const list = coAdmins.filter((_, i) => i !== idx);
    await saveCoAdmins(list);
  };

  const cardStyle = { background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '20px', marginBottom: '16px' };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <div style={{ fontWeight: 800, fontSize: '16px', color: T.text }}>👥 Co-Admins</div>
          <div style={{ fontSize: '12px', color: T.textMuted, marginTop: '2px' }}>Accès limités selon les permissions que tu choisis</div>
        </div>
        <button onClick={openAdd} style={{ padding: '8px 16px', borderRadius: '99px', border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}>➕ Ajouter</button>
      </div>

      {coAdmins.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '24px', color: T.textMuted, fontSize: '13px' }}>Aucun co-admin — clique ➕ pour en ajouter</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {coAdmins.map((a, idx) => (
            <div key={a.id || idx} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: dm ? '#0f172a' : '#f8fafc', borderRadius: '10px', border: `1px solid ${T.cardBorder}` }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#f59e0b,#ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>🛡️</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: '14px', color: T.text }}>{a.name || a.email}</div>
                <div style={{ fontSize: '11px', color: T.textMuted }}>{a.email}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                  {(a.permissions || []).map(p => {
                    const pDef = SA_PERMISSIONS.find(x => x.id === p);
                    return pDef ? (
                      <span key={p} style={{ fontSize: '10px', fontWeight: 700, background: '#eff6ff', color: '#1d4ed8', padding: '2px 7px', borderRadius: '99px' }}>
                        {pDef.emoji} {pDef.label}
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
              <button onClick={() => openEdit(idx)} style={{ padding: '6px 10px', borderRadius: '7px', background: '#eff6ff', color: '#1d4ed8', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>✏️</button>
              <button onClick={() => remove(idx)} style={{ padding: '6px 10px', borderRadius: '7px', background: '#fef2f2', color: '#ef4444', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}>🗑️</button>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div 
          onClick={e => e.target === e.currentTarget && setShowAdd(false)} 
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div style={{ background: dm ? '#1e293b' : 'white', borderRadius: '20px', padding: '28px', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ fontWeight: 900, fontSize: '18px', color: T.text }}>{editIdx !== null ? '✏️ Modifier co-admin' : '➕ Nouveau co-admin'}</div>
              <button onClick={() => setShowAdd(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: T.textMuted }}>×</button>
            </div>
            <div style={{ display: 'grid', gap: '12px', marginBottom: '16px' }}>
              {[
                { key: 'name', label: '👤 Nom', type: 'text' },
                { key: 'email', label: '✉️ Email', type: 'email' },
                { key: 'password', label: '🔑 Mot de passe', type: 'password' }
              ].map(k => (
                <div key={k.key}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: T.textSub, marginBottom: '4px' }}>{k.label}</label>
                  <input 
                    type={k.type} 
                    value={form[k.key] || ''} 
                    onChange={e => setForm(f => ({...f, [k.key]: e.target.value}))} 
                    style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: `2px solid ${T.cardBorder}`, background: T.input, color: T.text, fontSize: '14px', boxSizing: 'border-box' }} 
                  />
                </div>
              ))}
            </div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '12px', fontWeight: 700, color: T.textSub, marginBottom: '10px' }}>🔐 Permissions</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {SA_PERMISSIONS.map(p => (
                  <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', borderRadius: '8px', cursor: 'pointer', background: form.permissions.includes(p.id) ? '#eff6ff' : (dm ? '#0f172a' : '#f8fafc'), border: `1px solid ${form.permissions.includes(p.id) ? '#93c5fd' : T.cardBorder}` }}>
                    <input type="checkbox" checked={form.permissions.includes(p.id)} onChange={() => togglePerm(p.id)} style={{ accentColor: '#6366f1' }} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: form.permissions.includes(p.id) ? '#1d4ed8' : T.textSub }}>{p.emoji} {p.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setShowAdd(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: `2px solid ${T.cardBorder}`, background: 'transparent', color: T.text, fontWeight: 700, cursor: 'pointer' }}>Annuler</button>
              <button 
                onClick={save} 
                disabled={saving} 
                style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 700, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? '⏳...' : '💾 Sauvegarder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// SA: Global Config Panel
// ============================================
function SAGlobalConfigPanel({ T, dm }) {
  const [cfg, setCfg] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const { data } = await supabase.from('sa_settings').select('*').eq('id', 'global_config').single();
        if (data?.data) setCfg(data.data);
      } catch (_) {}
      setLoading(false);
    };
    fetchConfig();
  }, []);

  const set = (k, v) => setCfg(p => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await supabase.from('sa_settings').upsert({ id: 'global_config', data: { ...cfg, updatedAt: new Date().toISOString() } });
      window.__GLOBAL_CONFIG__ = cfg;
      alert('✅ Config globale sauvegardée!');
    } catch (e) { 
      alert('❌ ' + e.message); 
    }
    setSaving(false);
  };

  const cardStyle = { background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, padding: '24px', marginBottom: '16px' };
  const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 700, color: T.textSub, marginBottom: '5px' };
  const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '9px', border: `2px solid ${T.inputBorder || '#e2e8f0'}`, background: T.input || 'white', color: T.text, fontSize: '13px', boxSizing: 'border-box', fontFamily: 'monospace' };
  const descStyle = { fontSize: '11px', color: T.textMuted, marginTop: '4px' };

  const inp = (label, key, type = 'text', placeholder = '', desc = '') => (
    <div style={{ marginBottom: '14px' }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={cfg[key] || ''} placeholder={placeholder} onChange={e => set(key, e.target.value)} style={inputStyle} />
      {desc && <div style={descStyle}>{desc}</div>}
    </div>
  );

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: T.textMuted }}>⏳ Chargement...</div>;

  return (
    <div style={{ maxWidth: '680px' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '20px', fontWeight: 900, color: T.text }}>🌐 Config Globale</div>
        <div style={{ fontSize: '13px', color: T.textMuted, marginTop: '3px' }}>Ces paramètres s'appliquent à toutes les agences automatiquement</div>
      </div>

      {/* Telegram */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#0088cc,#00b4ff)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>✈️</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '15px', color: T.text }}>Telegram Bot</div>
            <div style={{ fontSize: '11px', color: T.textMuted }}>Token global — les agences ajoutent seulement leur Chat ID</div>
          </div>
        </div>
        {inp('🔑 Bot Token', 'telegramToken', 'text', 'bot123456789:AAF...', '')}
        <div style={{ padding: '10px 14px', background: dm ? '#172554' : '#eff6ff', borderRadius: '9px', fontSize: '12px', color: dm ? '#93c5fd' : '#1e40af', lineHeight: 1.6 }}>
          💡 Chaque agence configure uniquement son <b>Chat ID</b> dans ses paramètres.
          <br />Le Bot Token reste centralisé ici — les agences ne le voient pas.
        </div>
      </div>

      {/* GPS */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#10b981,#06b6d4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>📍</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '15px', color: T.text }}>GPS — Config par défaut</div>
            <div style={{ fontSize: '11px', color: T.textMuted }}>URL du serveur GPS partagé pour toutes les agences</div>
          </div>
        </div>
        {inp('🌐 GPS Server URL', 'gpsDefaultUrl', 'url', 'https://athuraya.ddns.net', '')}
        {inp('✉️ Email GPS par défaut', 'gpsDefaultEmail', 'email', 'admin@votre-gps.ma', '')}
      </div>

      {/* App Branding */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>🎨</div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '15px', color: T.text }}>App Branding</div>
            <div style={{ fontSize: '11px', color: T.textMuted }}>Nom et infos affichés dans l'application</div>
          </div>
        </div>
        {inp('📛 Nom de l\'app', 'appName', 'text', 'CAR TRACK APP', '')}
        {inp('📞 Téléphone support', 'supportPhone', 'text', '06...', '')}
        {inp('✉️ Email support', 'supportEmail', 'email', 'support@...', '')}
      </div>

      {/* Save Button */}
      <button 
        onClick={save} 
        disabled={saving}
        style={{ width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: saving ? '#a5b4fc' : 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: 'white', fontWeight: 800, fontSize: '15px', cursor: saving ? 'default' : 'pointer', boxShadow: '0 4px 15px rgba(99,102,241,0.4)' }}
      >
        {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder la config globale'}
      </button>

      <div style={{ marginTop: '12px', padding: '12px', background: dm ? '#1e293b' : '#f8fafc', borderRadius: '9px', fontSize: '12px', color: T.textMuted, textAlign: 'center' }}>
        🔒 Le mot de passe Super Admin est configuré via SUPER_ADMIN_PASSWORD sur le serveur
      </div>
    </div>
  );
}

// ============================================
// SA: Agency Edit Modal (full settings editor)
// ============================================
function SAAgencyEditModal({ reg, plans, onClose }) {
  const [data, setData] = useState({
    agencyName: reg.agencyName || reg.name || '',
    email: reg.email || '',
    phone: reg.phone || '',
    city: reg.data?.city || '',
    subscriptionPlan: reg.data?.subscriptionPlan || '',
    subscriptionEnd: reg.data?.subscriptionEnd || '',
    maxUsers: reg.data?.maxUsers || 1,
    status: reg.data?.status || (reg.status === 'rejected' ? 'suspended' : 'active'),
    notes: reg.data?.notes || '',
    password: '',
  });
  const [saving, setSaving] = useState(false);

  const inp = (label, field, type = 'text', placeholder = '') => (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px', color: '#475569' }}>{label}</label>
      <input
        type={type} 
        value={data[field]} 
        placeholder={placeholder}
        onChange={e => setData({ ...data, [field]: e.target.value })}
        style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
      />
    </div>
  );

  const save = async () => {
    if (!data.agencyName.trim()) return alert("Nom de l'agence requis");
    setSaving(true);
    try {
      const updatedData = {
        ...(reg.data || {}),
        agencyName: data.agencyName,
        name: data.agencyName,
        email: data.email,
        phone: data.phone,
        city: data.city,
        subscriptionPlan: data.subscriptionPlan,
        subscriptionEnd: data.subscriptionEnd,
        maxUsers: parseInt(data.maxUsers) || 1,
        status: data.status,
        notes: data.notes,
        updatedAt: new Date().toISOString(),
      };
      
      const topLevelStatus = data.status === 'suspended' ? 'rejected' : 'approved';

      // Persist via the secure SA endpoint (service role bypasses RLS and also
      // syncs the Supabase Auth email). A client-side update here would be
      // silently blocked by RLS (0 rows, no error) and never actually save.
      const saRes = await fetch('/api/sa/update-agency', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: reg.id,
          uid: reg.uid,
          email: (data.email || '').trim(),
          data: updatedData,
          status: topLevelStatus,
          saToken: __SA_TOKEN__ || '',
        }),
      });
      const saJson = await saRes.json().catch(() => ({}));
      if (!saRes.ok || !saJson.success) {
        throw new Error(saJson.error || 'Échec de la mise à jour (serveur)');
      }

      // Referral: if this agency was referred and is now ACTIVE, create the
      // referrer's reward credit (15 jours OU 200 DH) — once only. It then
      // appears automatically on the referrer's Parrainage page to be claimed.
      try {
        if (reg.data?.referredBy && !reg.data?.referralRewarded && data.status === 'active') {
          await grantReferralReward({
            supabase,
            referredAccount: {
              uid: reg.uid,
              referredBy: reg.data.referredBy,
              referralRewarded: reg.data.referralRewarded,
              agencyName: data.agencyName,
              email: data.email,
            },
          });
        }
      } catch (_) {}

      if (data.password.trim()) {
        const saToken = __SA_TOKEN__ || '';
        const pwRes = await fetch('/api/sa/reset-password', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: reg.uid, password: data.password.trim(), saToken })
        });
        if (!pwRes.ok) {
          const pwData = await pwRes.json();
          throw new Error('Mot de passe: ' + pwData.error);
        }
      }
      alert('✅ Configuration sauvegardée!');
      onClose();
    } catch (e) {
      alert('❌ ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div 
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui,sans-serif' }}
    >
      <div style={{ background: 'white', borderRadius: '20px', padding: '28px', maxWidth: '560px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <h2 style={{ fontWeight: 900, fontSize: '18px', margin: 0 }}>✏️ Modifier l'agence & abonnements</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ background: '#eff6ff', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#1e40af', fontWeight: 600 }}>
          🏢 {reg.agencyName || reg.email}
        </div>
        <div style={{ display: 'grid', gap: '12px' }}>
          {inp('🏢 Nom de l\'agence', 'agencyName', 'text', 'Nom agence...')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {inp('✉️ Email', 'email', 'email', 'email@...')}
            {inp('📞 Téléphone', 'phone', 'text', '06...')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {inp('📍 Ville', 'city', 'text', 'Casablanca...')}
            {inp('👥 Max utilisateurs', 'maxUsers', 'number')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px', color: '#475569' }}>📋 Plan d'abonnement</label>
              <select
                value={data.subscriptionPlan}
                onChange={e => {
                  const label = e.target.value;
                  const plan = plans.find(p => p.label === label);
                  let patch = { ...data, subscriptionPlan: label };
                  if (plan) {
                    // Auto-calc end date = today + (months × 30 days), from the
                    // day the subscription is set. And mark the agency active.
                    const d = new Date();
                    d.setDate(d.getDate() + (parseInt(plan.months, 10) || 1) * 30);
                    patch.subscriptionEnd = d.toISOString().split('T')[0];
                    patch.status = 'active';
                  }
                  setData(patch);
                }}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', background: 'white', boxSizing: 'border-box' }}
              >
                <option value="">-- Choisir plan --</option>
                {plans.map(p => <option key={p.id} value={p.label}>{`${p.icon || '🎯'} ${p.label}`}</option>)}
              </select>
              <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px' }}>📅 30 jours / mois — calculé auto</div>
            </div>
            {inp('📅 Fin abonnement (auto)', 'subscriptionEnd', 'date')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px', color: '#475569' }}>📊 Statut</label>
              <select
                value={data.status}
                onChange={e => setData({ ...data, status: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', background: 'white', boxSizing: 'border-box' }}
              >
                <option value="active">✅ Actif</option>
                <option value="suspended">🚫 Suspendu</option>
                <option value="trial">🔬 Essai</option>
                <option value="expired">⏰ Expiré</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, marginBottom: '4px', color: '#475569' }}>🔑 Nouveau mot de passe (optionnel)</label>
              <input
                type="password" 
                value={data.password} 
                placeholder="Laisser vide pour ne pas changer"
                onChange={e => setData({ ...data, password: e.target.value })}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '2px solid #e2e8f0', fontSize: '14px', boxSizing: 'border-box' }}
              />
            </div>
          </div>
          {inp('📝 Notes', 'notes', 'text', 'Notes internes...')}
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          <button onClick={onClose} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '2px solid #e2e8f0', background: 'white', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}>Annuler</button>
          <button 
            onClick={save} 
            disabled={saving}
            style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: saving ? '#93c5fd' : '#3b82f6', color: 'white', fontWeight: 700, fontSize: '14px', cursor: saving ? 'default' : 'pointer' }}
          >
            {saving ? '⏳ Sauvegarde...' : '💾 Sauvegarder'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SA: Renew Subscription Modal
// ============================================
function SARenewModal({ account, plans, onClose }) {
  const [selectedPlan, setSelectedPlan] = useState('');
  const [saving, setSaving] = useState(false);

  const renew = async () => {
    if (!selectedPlan) return alert('Choisissez un plan');
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan) return;

    setSaving(true);
    try {
      const currentEnd = account.subscriptionEnd && new Date(account.subscriptionEnd) > new Date()
        ? new Date(account.subscriptionEnd)
        : new Date();
      
      const newEnd = new Date(currentEnd);
      newEnd.setMonth(newEnd.getMonth() + plan.months);
      const newEndStr = newEnd.toISOString().split('T')[0];

      const prevPayments = account.payments || [];
      const { data: existing } = await supabase.from('user_registrations').select('data').eq('uid', account.uid).single();
      const merged = {
        ...(existing?.data || {}),
        subscriptionPlan: plan.label,
        subscriptionEnd: newEndStr,
        status: 'active',
        // Reactivation: clear any auto-suspension and reset the reminder cycle.
        suspended: false,
        suspendedReason: null,
        suspendedAt: null,
        reminder5Sent: false,
        reminder2Sent: false,
        reactivatedAt: new Date().toISOString(),
        lastRenewedAt: new Date().toISOString(),
        lastRenewedPlan: plan.label,
        payments: [...prevPayments, { amount: plan.price, plan: plan.label, date: new Date().toISOString() }]
      };
      // Restore access: data + top-level status='approved' (AuthContext gate).
      await supabase.from('user_registrations').update({ data: merged, status: 'approved' }).eq('uid', account.uid);

      // Referral reward: if THIS was the agency's first payment and it was
      // referred, grant the referrer their free bonus days (once only).
      let referralMsg = '';
      try {
        if (prevPayments.length === 0 && account.referredBy && !account.referralRewarded) {
          const res = await grantReferralReward({ supabase, referredAccount: account });
          if (res.granted) referralMsg = `\n🎁 Parrainage: crédit créé pour le parrain (${res.freeDays} jours OU ${res.cashAmount} ${referralCurrency}).`;
        }
      } catch (_) {}

      // Also reactivate via the admin API (service role) — works even under
      // strict RLS where the client cannot write another agency's row.
      try {
        await fetch('/api/sa/reactivate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid: account.uid, subscriptionEnd: newEndStr, subscriptionPlan: plan.label, saToken: __SA_TOKEN__ }),
        });
      } catch (_) {}

      alert(`✅ Abonnement renouvelé!\n📅 Nouveau: ${newEnd.toLocaleDateString('fr-FR')}${referralMsg}`);
      onClose();
    } catch (e) {
      alert('❌ ' + e.message);
    }
    setSaving(false);
  };

  const currentEnd = account.subscriptionEnd;
  const isExpired = currentEnd && new Date(currentEnd) < new Date();

  return (
    <div 
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui, sans-serif' }}
    >
      <div style={{ background: 'white', borderRadius: '20px', padding: '28px', maxWidth: '480px', width: '100%' }}>
        <h2 style={{ fontWeight: 900, marginBottom: '6px', fontSize: '18px' }}>🔄 Renouveler l'abonnement</h2>
        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '20px' }}>
          Agence: {account.agencyName}
        </p>

        {/* Current status */}
        <div style={{ padding: '12px 16px', borderRadius: '10px', background: isExpired ? '#fee2e2' : '#f0fdf4', border: `1px solid ${isExpired ? '#fca5a5' : '#86efac'}`, marginBottom: '20px', fontSize: '13px' }}>
          {isExpired
            ? `❌ Abonnement expiré depuis le ${currentEnd ? new Date(currentEnd).toLocaleDateString('fr-FR') : 'jamais'}`
            : `✅ Actif jusqu'au ${currentEnd ? new Date(currentEnd).toLocaleDateString('fr-FR') : 'indéfini'}`}
        </div>

        {/* Plan selector */}
        <div style={{ display: 'grid', gap: '10px', marginBottom: '20px' }}>
          {plans.map(plan => (
            <label 
              key={plan.id}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
                borderRadius: '12px', border: `2px solid ${selectedPlan === plan.id ? '#667eea' : '#e2e8f0'}`,
                background: selectedPlan === plan.id ? '#eff6ff' : 'white', cursor: 'pointer'
              }}
            >
              <input type="radio" name="plan" value={plan.id} checked={selectedPlan === plan.id} onChange={() => setSelectedPlan(plan.id)} style={{ accentColor: '#667eea' }} />
              <span style={{ fontSize: '24px' }}>{plan.icon || '🎯'}</span>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: '14px', fontWeight: 800 }}>{plan.label}</span>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Durée: {plan.months} mois</div>
              </div>
              <span style={{ fontSize: '16px', fontWeight: 900, color: '#667eea' }}>{plan.price} MAD</span>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#f1f5f9', color: '#475569', fontWeight: 700, cursor: 'pointer' }}
          >
            Annuler
          </button>
          <button
            onClick={renew} 
            disabled={saving || !selectedPlan}
            style={{ flex: 2, padding: '12px', borderRadius: '10px', border: 'none', background: selectedPlan ? 'linear-gradient(135deg, #10b981, #059669)' : '#e2e8f0', color: selectedPlan ? 'white' : '#94a3b8', fontWeight: 700, cursor: saving || !selectedPlan ? 'default' : 'pointer' }}
          >
            {saving ? '⏳...' : '✅ Renouveler'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// SA: Users & Staff Tab — platform-wide overview
// ============================================
function SAUsersStaffTab({ T, dm, registrations, accounts, deleteAgency, setEditAgency, setImpersonating, setShowRenew, plans, __SA_TOKEN__ }) {
  const [activeSubTab, setActiveSubTab] = useState('admins'); // admins | staff
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [resettingPw, setResettingPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Load all staff across all agencies
  const { data: allStaff, loading: loadingStaff, remove: removeStaff, update: updateStaff } = useCollection('staff', { bypassAgencyFilter: true });

  const handleResetPassword = async (uid) => {
    if (!newPassword.trim() || newPassword.length < 6) {
      return alert("Le mot de passe doit comporter au moins 6 caractères.");
    }
    setResettingPw(uid);
    try {
      const saToken = __SA_TOKEN__ || '';
      const res = await fetch('/api/sa/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, password: newPassword.trim(), saToken })
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Erreur serveur');
      }
      alert('✅ Mot de passe réinitialisé avec succès!');
      setNewPassword('');
    } catch (e) {
      alert('❌ Erreur: ' + e.message);
    } finally {
      setResettingPw(null);
    }
  };

  const getAgencyName = (agencyId) => {
    const reg = registrations.find(r => r.uid === agencyId);
    return reg ? (reg.agencyName || reg.name) : agencyId;
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif' }}>
      {/* Title */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 900, color: T.text, margin: 0 }}>👥 Utilisateurs & Personnel</h2>
        <p style={{ fontSize: '13px', color: T.textSub, marginTop: '2px' }}>Gérez les comptes administrateurs d'agences et le personnel de la plateforme</p>
      </div>

      {/* Toggle Sub-Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: `1px solid ${T.divider}`, paddingBottom: '10px' }}>
        <button
          onClick={() => { setActiveSubTab('admins'); setSearchQuery(''); }}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: 800, fontSize: '13px', cursor: 'pointer',
            background: activeSubTab === 'admins' ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'transparent',
            color: activeSubTab === 'admins' ? 'white' : T.textSub
          }}
        >
          🔑 Propriétaires & Agences ({registrations.filter(r => r.status === 'approved').length})
        </button>
        <button
          onClick={() => { setActiveSubTab('staff'); setSearchQuery(''); }}
          style={{
            padding: '8px 16px', borderRadius: '8px', border: 'none', fontWeight: 800, fontSize: '13px', cursor: 'pointer',
            background: activeSubTab === 'staff' ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'transparent',
            color: activeSubTab === 'staff' ? 'white' : T.textSub
          }}
        >
          👔 Personnel des Agences ({allStaff ? allStaff.length : 0})
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '16px' }}>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder={activeSubTab === 'admins' ? "🔍 Rechercher par agence, email, ville..." : "🔍 Rechercher par nom, rôle, téléphone..."}
          style={{ width: '100%', maxWidth: '360px', padding: '10px 16px', borderRadius: '10px', border: `1px solid ${T.inputBorder}`, background: T.input, color: T.text, fontSize: '13px', outline: 'none' }}
        />
      </div>

      {/* VIEW: ADMINS */}
      {activeSubTab === 'admins' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {accounts
            .filter(a => !searchQuery.trim() || 
              (a.agencyName || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
              (a.email || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
              (a.city || '').toLowerCase().includes(searchQuery.toLowerCase())
            )
            .map(acc => {
              const hasLogs = acc.data?.loginHistory && acc.data.loginHistory.length > 0;
              const isLogsExpanded = expandedLogId === acc.uid;
              const isResetting = resettingPw === acc.uid;
              return (
                <div key={acc.uid} style={{ background: T.card, borderRadius: '14px', border: `1px solid ${T.cardBorder}`, padding: '16px 20px', overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: 'linear-gradient(135deg,#6366f1,#3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: 'white' }}>🏢</div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: '14px', color: T.text }}>{acc.agencyName}</div>
                        <div style={{ fontSize: '12px', color: T.textSub }}>{acc.email} • 📞 {acc.phone || 'Non renseigné'}</div>
                        <div style={{ fontSize: '11px', color: T.textMuted, marginTop: '2px' }}>Plan: <strong>{acc.subscriptionPlan || 'Néant'}</strong> • Fin: {acc.subscriptionEnd ? new Date(acc.subscriptionEnd).toLocaleDateString('fr-FR') : 'Indéterminée'}</div>
                      </div>
                    </div>

                    {/* Actions panel */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => setImpersonating(acc)}
                        title="Impersoner l'agence"
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        👥 Impersoner
                      </button>
                      <button
                        onClick={() => setEditAgency(registrations.find(r => r.uid === acc.uid))}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        onClick={() => setShowRenew(acc)}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        🔄 Renouveler
                      </button>
                      <button
                        onClick={() => setExpandedLogId(isLogsExpanded ? null : acc.uid)}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: T.hover, color: T.textSub, border: `1px solid ${T.cardBorder}`, fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        🕐 Activité {hasLogs ? `(${acc.data.loginHistory.length})` : ''}
                      </button>
                      <button
                        onClick={() => setResettingPw(isResetting ? null : acc.uid)}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#faf5ff', color: '#6b21a8', border: '1px solid #e9d5ff', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        🔑 MDP
                      </button>
                      <button
                        onClick={() => deleteAgency(acc.uid)}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#fef2f2', color: '#b91c1c', border: '1px solid #fee2e2', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Reset Password Form */}
                  {isResetting && (
                    <div style={{ marginTop: '12px', borderTop: `1px solid ${T.divider}`, paddingTop: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        type="password"
                        placeholder="Nouveau mot de passe (min 6 car.)"
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        style={{ padding: '6px 12px', borderRadius: '8px', border: `1px solid ${T.inputBorder}`, background: T.input, color: T.text, fontSize: '12px', width: '220px', outline: 'none' }}
                      />
                      <button
                        onClick={() => handleResetPassword(acc.uid)}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#6b21a8', color: 'white', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        Enregistrer
                      </button>
                      <button
                        onClick={() => { setResettingPw(null); setNewPassword(''); }}
                        style={{ padding: '6px 12px', borderRadius: '8px', background: '#f1f5f9', color: '#475569', border: 'none', fontWeight: 700, fontSize: '12px', cursor: 'pointer' }}
                      >
                        Annuler
                      </button>
                    </div>
                  )}

                  {/* Activity Logs List */}
                  {isLogsExpanded && (
                    <div style={{ marginTop: '12px', borderTop: `1px solid ${T.divider}`, paddingTop: '12px' }}>
                      <div style={{ fontWeight: 700, fontSize: '12px', color: T.textSub, marginBottom: '6px' }}>🕐 Historique de connexion (Derniers accès):</div>
                      {hasLogs ? (
                        <div style={{ display: 'grid', gap: '4px' }}>
                          {acc.data.loginHistory.map((log, index) => (
                            <div key={index} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: dm ? '#0f172a' : '#f8fafc', borderRadius: '6px', fontSize: '11px' }}>
                              <span style={{ fontWeight: 600, color: T.textSub }}>{new Date(log.timestamp).toLocaleString('fr-FR')}</span>
                              <span style={{ color: T.textMuted, maxWidth: '65%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={log.userAgent}>{log.userAgent}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: '11px', color: T.textMuted, fontStyle: 'italic' }}>Aucun historique enregistré pour ce compte.</div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {/* VIEW: STAFF */}
      {activeSubTab === 'staff' && (
        <div style={{ background: T.card, borderRadius: '16px', border: `1px solid ${T.cardBorder}`, overflow: 'hidden' }}>
          {loadingStaff ? (
            <div style={{ padding: '40px', display: 'flex', justifyContent: 'center' }}>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
                <thead style={{ background: dm ? '#0f172a' : '#f8fafc', borderBottom: `1px solid ${T.divider}` }}>
                  <tr>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Nom</th>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Rôle</th>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Agence</th>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Contact</th>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Rémunération</th>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Statut</th>
                    <th style={{ padding: '12px 16px', color: T.textSub, fontWeight: 700 }}>Actions</th>
                  </tr>
                </thead>
                <tbody style={{ divideY: `1px solid ${T.divider}` }}>
                  {(allStaff || [])
                    .filter(s => !searchQuery.trim() || 
                      (s.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                      (s.role || '').toLowerCase().includes(searchQuery.toLowerCase()) || 
                      (s.phone || '').toLowerCase().includes(searchQuery.toLowerCase())
                    )
                    .map(s => (
                      <tr key={s.id} style={{ borderBottom: `1px solid ${T.divider}` }}>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: T.text }}>{s.name}</td>
                        <td style={{ padding: '12px 16px', color: T.textSub }}>{s.role}</td>
                        <td style={{ padding: '12px 16px', fontWeight: 600, color: '#4f46e5' }}>🏢 {getAgencyName(s.agency_id)}</td>
                        <td style={{ padding: '12px 16px', color: T.textSub }}>{s.phone} <br /> <span style={{ fontSize: '10px', color: T.textMuted }}>{s.email}</span></td>
                        <td style={{ padding: '12px 16px', fontWeight: 700, color: T.text }}>
                          {s.salaryAmount?.toLocaleString()} MAD <span style={{ fontSize: '10px', color: T.textMuted }}>({s.salaryType === 'monthly' ? 'Fixe' : 'Jour'})</span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ padding: '2px 8px', borderRadius: '99px', fontSize: '10px', fontWeight: 700, background: s.status === 'active' ? '#dcfce7' : '#fee2e2', color: s.status === 'active' ? '#15803d' : '#991b1b' }}>
                            {s.status === 'active' ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <button
                            onClick={async () => {
                              if (!confirm('Supprimer ce membre du personnel ?')) return;
                              await removeStaff(s.id);
                              alert('✅ Employé supprimé.');
                            }}
                            style={{ border: 'none', background: '#fee2e2', color: '#ef4444', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }}
                          >
                            🗑️ Supprimer
                          </button>
                        </td>
                      </tr>
                    ))}
                  {(allStaff || []).length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: T.textMuted, fontStyle: 'italic' }}>Aucun membre du personnel enregistré.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
